/**
 * QUANTUM DAY — Main Public Website
 * FIXED: search 1-char bug (components moved outside App),
 *        footer company links, footer navy colors, email verification
 */
import { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_API_BASE || "";

/* ── API LAYER ─────────────────────────────────────────── */
const db = {
  async articles() {
    const r = await fetch(`${API}/api/articles`);
    if (!r.ok) throw new Error();
    return r.json();
  },
  async conferences() {
    const r = await fetch(`${API}/api/conferences`);
    if (!r.ok) throw new Error();
    return r.json();
  },
  async settings() {
    const r = await fetch(`${API}/api/settings`);
    if (!r.ok) throw new Error();
    const m = await r.json();
    let siteImages = {};
    try { siteImages = typeof m.site_images === "string" ? JSON.parse(m.site_images) : (m.site_images || {}); } catch {}
    return {
      siteName: m.site_name || "Quantum Day",
      tagline: m.tagline || "Intelligence at the Edge of Quantum",
      description: m.description || "Your premier source for quantum computing news.",
      keywords: m.keywords || "quantum",
      siteImages,
    };
  },
  async menus() {
    const r = await fetch(`${API}/api/menus`);
    if (!r.ok) throw new Error();
    return r.json();
  },
};

/* ── ICONS ─────────────────────────────────────────────── */
const SVG = {
  search:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  menu:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  x:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  calendar:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  pin:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  users:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  extLink: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  alert:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  fb:      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>,
  tw:      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>,
  check:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  mail:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
};
const Ico = ({ name, size = 16, style = {} }) =>
  <span style={{ width: size, height: size, display: "inline-flex", flexShrink: 0, ...style }}>{SVG[name]}</span>;
const fmt  = d => d ? new Date(d).toLocaleDateString("en-US", { month: "long",  day: "numeric", year: "numeric" }) : "";
const fmtS = d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

/* ── CSS ────────────────────────────────────────────────── */
const CSS = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}
body{
  background:var(--bg);
  color:var(--t);
  font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Helvetica Neue",Arial,sans-serif;
  font-size:15px;
  line-height:1.6;
  -webkit-font-smoothing:antialiased;
  transition:background .25s ease,color .25s ease;
}
a{color:inherit;text-decoration:none;}
button{font-family:inherit;cursor:pointer;}
img{display:block;}
::-webkit-scrollbar{width:5px;}
::-webkit-scrollbar-thumb{background:rgba(60,60,67,.25);border-radius:3px;}
:root{
  --navy:#2a4f7c;
  --navy-d:#1f3d60;
  --red:#c0392b;
  --bd:rgba(60,60,67,.12);
  --bd2:rgba(60,60,67,.07);
  --bg:#f4f3f1;
  --bg2:#eceae6;
  --bg3:#e4e1dc;
  --s:#f9f8f6;
  --s2:#ffffff;
  --t:#111112;
  --t2:#2c2c2e;
  --t3:#6b6b70;
  --t4:#98989d;
  --ac:#2a4f7c;
  --ac2:#e8eef5;
  --serif:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;
  --max:1140px;
  --sh:0 1px 3px rgba(0,0,0,.05),0 1px 2px rgba(0,0,0,.04);
  --sh2:0 4px 20px rgba(0,0,0,.08),0 1px 4px rgba(0,0,0,.04);
  --sh3:0 8px 32px rgba(0,0,0,.1),0 2px 8px rgba(0,0,0,.05);
  --dm-transition:background .25s ease,color .25s ease,border-color .25s ease,box-shadow .25s ease;
}

/* ── DARK MODE ───────────────────────────────────────────── */
html.dark,body.dark{
  color-scheme:dark;
}
body.dark{
  --bd:rgba(255,255,255,.1);
  --bd2:rgba(255,255,255,.06);
  --bg:#0f0f10;
  --bg2:#1a1a1c;
  --bg3:#222224;
  --s:#161618;
  --s2:#1c1c1e;
  --t:#f2f2f7;
  --t2:#ebebf0;
  --t3:#98989d;
  --t4:#636366;
  --ac2:rgba(42,79,124,.25);
  --sh:0 1px 3px rgba(0,0,0,.3),0 1px 2px rgba(0,0,0,.2);
  --sh2:0 4px 20px rgba(0,0,0,.5),0 1px 4px rgba(0,0,0,.3);
  --sh3:0 8px 32px rgba(0,0,0,.6),0 2px 8px rgba(0,0,0,.4);
}
/* transitions handled per-element */
body.dark ::-webkit-scrollbar-thumb{background:rgba(255,255,255,.15);}

/* dark — top bar */
body.dark .tb{background:#090c10;}

/* dark — masthead */
body.dark .mh{background:var(--s2);border-bottom-color:var(--bd);}

/* dark — nav */
body.dark .nav{background:var(--s2);border-bottom-color:var(--bd);}
body.dark .nav.scrolled{background:rgba(28,28,30,.96);}

/* dark — search overlay */
body.dark .srch-ov{background:rgba(0,0,0,.75);}
body.dark .srch-box{background:var(--s2);border-color:var(--bd);}
body.dark .srch-box input{color:var(--t);}

/* dark — mobile nav */
body.dark .mob-nav{background:rgba(22,22,24,.98);}
body.dark .mob-item{color:var(--t2);}

/* dark — cards & surfaces */
body.dark .feat{background:var(--s2);border-color:var(--bd);}
body.dark .cc{background:var(--s2);border-color:var(--bd);}
body.dark .ctp-form{background:var(--s2);border-color:var(--bd);}
body.dark .ctp-card{background:var(--s2);border-color:var(--bd);}
body.dark .nl-compact{background:rgba(42,79,124,.15);border-color:rgba(42,79,124,.25);}
body.dark .more-card{background:var(--s2);border-color:var(--bd);}

/* dark — newsletter banner */
body.dark .nl-banner{background:#0d1a2e;}

/* dark — page background areas */
body.dark .wrap{background:transparent;}
body.dark .two-col{background:transparent;}
body.dark main{background:transparent;}
body.dark aside{background:transparent;}
body.dark .ad-wrap{background:transparent;}

/* dark — article list items */
body.dark .ali{border-bottom-color:var(--bd);}

/* dark — section headers */
body.dark .sec-hd{border-bottom-color:var(--bd);}

/* dark — article body text (key fix) */
body.dark .ad-body p{color:var(--t2);}
body.dark .ad-body p:first-child::first-letter{color:var(--navy);}
body.dark .ad-title{color:var(--t);}
body.dark .ad-crumb{color:var(--t4);}
body.dark .ad-auth{color:var(--t);}
body.dark .ad-byline{border-color:var(--bd);}
body.dark .ad-cap{color:var(--t4);}
body.dark .ad-img{box-shadow:var(--sh);}

/* dark — more-sec section */
body.dark .more-sec{background:var(--bg2);border-top-color:var(--bd);}
body.dark .yml-sec{background:var(--bg);}
body.dark .yml-card{background:var(--bg2);border-color:var(--bd);}
body.dark .yml-card:hover{box-shadow:0 8px 24px rgba(0,0,0,.3);}
body.dark .yml-match-badge{background:rgba(42,79,124,.25);color:#93c5fd;}

/* dark — skel shimmer */
body.dark .skel{background:linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%);}

/* dark — filter bar */
body.dark .fbar{background:var(--s2);}

/* dark — search bar */
body.dark .sbar .swrap{background:var(--s2);}

/* dark — related/trending items */
body.dark .rel-item{border-bottom-color:var(--bd);}

body.dark .tr-item{border-bottom-color:var(--bd);}
body.dark .cs-item{border-bottom-color:var(--bd);}

/* dark — category buttons */
body.dark .cat-btn:hover{background:var(--bg3);}
body.dark .cat-btn.on{background:rgba(42,79,124,.3);}

/* dark — tag chips */
body.dark .cc-tag{background:var(--bg2);border-color:var(--bd);}
body.dark .ad-tag{background:var(--bg2);border-color:var(--bd);}
body.dark .ad-tag:hover{background:rgba(42,79,124,.4);color:#93c5fd;border-color:rgba(42,79,124,.6);}
body.dark .tag-chip{background:var(--bg2);border-color:var(--bd);}
body.dark .tag-chip:hover,body.dark .tag-chip.on{background:rgba(42,79,124,.4);color:#93c5fd;border-color:rgba(42,79,124,.6);}

/* dark — contact form page */
body.dark .ctp{background:transparent;}

/* dark — newsletter OK inline */
body.dark .nl-ok{background:rgba(52,134,58,.18);border-color:rgba(52,134,58,.35);color:#86efac;}

/* dark — sb newsletter compact */
body.dark .nl-compact{background:rgba(42,79,124,.18);border-color:rgba(42,79,124,.3);}

/* dark — empty state */
body.dark .empty h3{color:var(--t3);}

/* dark — error box */
body.dark .err-box{background:rgba(192,57,43,.12);border-color:rgba(192,57,43,.3);}

/* dark — share buttons */
body.dark .sh-tw{background:#222;}

/* dark — footer already dark, just soften */
body.dark .ft{background:#0d1e33;border-top-color:rgba(255,255,255,.05);}

/* dark — inputs */
body.dark .cf-inp{background:var(--bg2);border-color:var(--bd);color:var(--t);}
body.dark .cf-inp:focus{background:var(--s2);}
body.dark .nl-inp{background:var(--bg2);border-color:var(--bd);color:var(--t);}
body.dark .nl-banner .nl-inp{background:rgba(255,255,255,.08);}
body.dark .swrap{background:var(--s2);border-color:var(--bd);}
body.dark .swrap input{color:var(--t);}

/* dark — share buttons */
body.dark .sh-cp{background:var(--bg2);color:var(--t2);border-color:var(--bd);}

/* dark — pagination */
body.dark .pg-hd{background:var(--s2);}

/* dark — progress bar track */
body.dark .rpb-track{background:rgba(255,255,255,.08);}

/* dark — sidebar */
body.dark .nl-ok{background:rgba(52,134,58,.15);border-color:rgba(52,134,58,.3);}

/* ── DARK MODE TOGGLE BUTTON ─────────────────────────────── */
.dm-toggle{
  background:none;
  border:1px solid var(--bd);
  border-radius:7px;
  padding:7px 9px;
  color:var(--t3);
  display:flex;align-items:center;
  transition:all .18s;
  cursor:pointer;
  position:relative;
  overflow:hidden;
}
.dm-toggle:hover{
  background:var(--bg2);
  border-color:var(--bd);
  color:var(--t);
}
.dm-toggle svg{
  transition:transform .35s cubic-bezier(.34,1.56,.64,1), opacity .2s;
}
.dm-icon-sun{
  position:absolute;
  opacity:0;
  transform:rotate(-90deg) scale(.6);
}
.dm-icon-moon{
  opacity:1;
  transform:rotate(0deg) scale(1);
}
body.dark .dm-icon-sun{
  opacity:1;
  transform:rotate(0deg) scale(1);
  position:relative;
}
body.dark .dm-icon-moon{
  opacity:0;
  transform:rotate(90deg) scale(.6);
  position:absolute;
}

/* ── VIEW COUNT ─────────────────────────────────────────── */
.vc{
  display:inline-flex;align-items:center;gap:4px;
  font-size:11px;color:var(--t4);font-weight:500;
}
.vc svg{flex-shrink:0;opacity:.7;}

/* ── READING PROGRESS BAR ───────────────────────────────── */
.rpb-track{
  height:3px;
  background:var(--bd2);
  width:100%;
  overflow:hidden;
}
.rpb{
  height:100%;
  width:0%;
  background:linear-gradient(90deg,var(--navy) 0%,#4a90d9 55%,var(--navy-d) 100%);
  transition:width .08s linear;
  border-radius:0 2px 2px 0;
  box-shadow:2px 0 8px rgba(42,79,124,.4);
  position:relative;
}
.rpb-glow{
  position:absolute;right:-4px;top:50%;transform:translateY(-50%);
  width:8px;height:8px;border-radius:50%;
  background:rgba(74,144,217,.9);
  box-shadow:0 0 6px 2px rgba(74,144,217,.5);
}

/* ── ANIMATIONS ─────────────────────────────────────────── */
@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
@keyframes floatUp{0%{opacity:0;transform:translateY(22px)}100%{opacity:1;transform:translateY(0)}}
.fi{animation:fadeUp .3s ease both;}

/* ── TOP BAR ─────────────────────────────────────────────── */
.tb{
  background:#0d1f33;
  height:32px;
  display:flex;
  align-items:center;
  border-bottom:1px solid rgba(255,255,255,.06);
}
.tb-in{
  max-width:var(--max);margin:0 auto;padding:0 24px;
  width:100%;display:flex;align-items:center;justify-content:space-between;
}
.tb-date{font-size:11px;color:rgba(174,174,178,.7);letter-spacing:.3px;}
.tb-soc{display:flex;gap:14px;align-items:center;}
.tb-soc a{color:rgba(174,174,178,.5);display:flex;align-items:center;transition:color .2s;}
.tb-soc a:hover{color:rgba(255,255,255,.7);}

/* ── MASTHEAD ────────────────────────────────────────────── */
.mh{
  background:var(--s2);
  border-bottom:1px solid var(--bd);
  box-shadow:var(--sh);
}
.mh-in{
  max-width:var(--max);margin:0 auto;padding:14px 24px;
  display:flex;align-items:center;justify-content:space-between;gap:16px;
}
.mh-brand{cursor:pointer;line-height:1;animation:slideDown .45s ease both;}
.mh-name{
  font-family:var(--serif);
  font-size:clamp(20px,3vw,34px);
  font-weight:700;color:var(--navy);
  letter-spacing:-1px;line-height:1;
  transition:color .2s;
}
.mh-name:hover{color:var(--navy-d);}
.mh-tag{font-size:9px;letter-spacing:2.5px;text-transform:uppercase;color:var(--t4);margin-top:5px;font-weight:500;}
.mh-right{display:flex;align-items:center;gap:6px;animation:slideDown .45s .08s ease both;opacity:0;animation-fill-mode:forwards;}
.mh-srch{
  background:none;border:1px solid transparent;border-radius:7px;
  padding:7px 9px;color:var(--t3);display:flex;align-items:center;transition:all .18s;cursor:pointer;
}
.mh-srch:hover{background:var(--bg2);border-color:var(--bd);color:var(--navy);}
.mh-sub{
  background:var(--navy);color:#dce8f5;border:none;
  border-radius:7px;padding:8px 18px;
  font-size:11.5px;font-weight:600;letter-spacing:.4px;
  transition:all .18s;white-space:nowrap;cursor:pointer;
  box-shadow:0 2px 8px rgba(42,79,124,.25);
}
.mh-sub:hover{background:var(--navy-d);box-shadow:0 4px 14px rgba(42,79,124,.35);transform:translateY(-1px);}
.mh-sub:active{transform:translateY(0);}
.mh-hbtn{display:none;background:none;border:1px solid var(--bd);border-radius:7px;padding:6px 8px;color:var(--navy);cursor:pointer;transition:all .15s;}
.mh-hbtn:hover{background:var(--bg2);}
@media(max-width:680px){.mh-hbtn{display:flex;align-items:center;}}

/* ── SEARCH OVERLAY ──────────────────────────────────────── */
.srch-ov{
  position:fixed;inset:0;
  background:rgba(28,28,30,.6);
  z-index:999;display:flex;align-items:flex-start;justify-content:center;
  padding-top:72px;backdrop-filter:blur(8px);
  animation:fadeIn .18s ease;
}
.srch-box{
  background:var(--s2);width:100%;max-width:600px;
  border-radius:12px;overflow:hidden;
  box-shadow:var(--sh3);border:1px solid var(--bd);
}
.srch-box input{
  width:100%;padding:18px 20px;font-size:17px;
  border:none;outline:none;color:var(--t);
  font-family:inherit;background:transparent;
}
.srch-hint{padding:0 20px 14px;font-size:12px;color:var(--t4);border-top:1px solid var(--bd2);}

/* ── PRIMARY NAV ─────────────────────────────────────────── */
.nav{
  background:var(--s2);
  border-bottom:none;
  position:sticky;top:0;z-index:200;
  transition:all .22s;
}
.nav.scrolled{
  box-shadow:0 4px 20px rgba(0,0,0,.07);
  background:rgba(250,250,248,.96);
  backdrop-filter:blur(12px);
  -webkit-backdrop-filter:blur(12px);
}
.nav-in{
  max-width:var(--max);margin:0 auto;padding:0 24px;
  display:flex;align-items:center;overflow-x:auto;
  scrollbar-width:none;gap:0;
}
.nav-in::-webkit-scrollbar{display:none;}
.nav-lnk{
  padding:11px 15px;font-size:11px;font-weight:600;
  color:var(--t2);cursor:pointer;white-space:nowrap;
  position:relative;transition:color .18s;
  letter-spacing:.7px;text-transform:uppercase;
}
.nav-lnk::after{
  content:'';position:absolute;bottom:0;
  left:50%;right:50%;height:2px;
  background:var(--navy);transition:all .2s;border-radius:2px 2px 0 0;
}
.nav-lnk:hover{color:var(--t);}
.nav-lnk:hover::after{left:15px;right:15px;}
.nav-lnk.on{color:var(--navy);font-weight:700;}
.nav-lnk.on::after{left:15px;right:15px;}

/* ── MOBILE NAV ──────────────────────────────────────────── */
.mob-nav{
  position:fixed;inset:0;
  background:rgba(250,250,248,.98);
  backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);
  z-index:900;padding:22px;overflow-y:auto;animation:fadeIn .18s ease;
}
.mob-head{
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom:26px;padding-bottom:14px;border-bottom:1px solid var(--bd);
}
.mob-logo{font-family:var(--serif);font-size:22px;font-weight:700;color:var(--navy);letter-spacing:-.5px;}
.mob-close{background:var(--bg2);border:1px solid var(--bd);border-radius:7px;padding:6px 8px;cursor:pointer;color:var(--t2);transition:all .15s;}
.mob-close:hover{background:var(--bg3);}
.mob-item{
  display:block;padding:14px 0;font-size:15px;font-weight:500;
  border-bottom:1px solid var(--bd2);cursor:pointer;color:var(--t2);transition:color .15s;
}
.mob-item:hover{color:var(--navy);}

/* ── LAYOUT ──────────────────────────────────────────────── */
.wrap{max-width:var(--max);margin:0 auto;padding:0 20px;}
.two-col{display:grid;grid-template-columns:1fr 288px;gap:40px;padding:26px 0 0;}
@media(max-width:880px){.two-col{grid-template-columns:1fr;}}

/* ── SECTION HEADER ──────────────────────────────────────── */
.sec-hd{display:flex;align-items:center;gap:10px;margin-bottom:18px;padding-bottom:8px;border-bottom:2px solid var(--bd);}
.sec-lbl{
  font-size:10.5px;font-weight:700;letter-spacing:1.5px;
  text-transform:uppercase;color:var(--navy);
  white-space:nowrap;
}
.sec-line{flex:1;height:1px;background:transparent;}

/* ── FEATURED ARTICLE ────────────────────────────────────── */
.feat{
  background:var(--s2);border:1px solid var(--bd);
  border-radius:12px;overflow:hidden;margin-bottom:26px;
  box-shadow:var(--sh);transition:box-shadow .25s;
}
.feat:hover{box-shadow:var(--sh2);}
.feat-img-wrap{position:relative;overflow:hidden;}
.feat-img-wrap img,.feat-img-wrap>div{transition:transform .55s ease;}
.feat:hover .feat-img-wrap img,.feat:hover .feat-img-wrap>div{transform:scale(1.02);}
.feat-body{padding:18px 20px 20px;border-top:1px solid var(--bd2);}
.feat-cat{
  display:inline-block;background:var(--navy);color:#dce8f5;
  font-size:9px;font-weight:700;letter-spacing:1.2px;
  text-transform:uppercase;padding:3px 9px;border-radius:4px;margin-bottom:9px;
}
.feat-title{
  font-family:var(--serif);font-size:clamp(17px,2.4vw,24px);
  font-weight:700;color:var(--t);line-height:1.28;cursor:pointer;
  transition:color .15s;margin-bottom:9px;letter-spacing:-.3px;
}
.feat-title:hover{color:var(--navy);}
.feat-ex{font-size:14px;color:var(--t2);line-height:1.7;margin-bottom:12px;}
.feat-meta{display:flex;flex-wrap:wrap;gap:10px;font-size:11.5px;color:var(--t4);}

/* ── ARTICLE LIST ITEM ───────────────────────────────────── */
.ali{
  padding:18px 0;border-bottom:1px solid var(--bd2);
  display:flex;gap:15px;align-items:flex-start;
  transition:opacity .15s;
}
.ali:first-child{padding-top:0;}
.ali:last-child{border-bottom:none;}
.ali:hover{opacity:.9;}
.ali-thumb{
  width:104px;height:70px;border-radius:8px;
  overflow:hidden;flex-shrink:0;
  box-shadow:var(--sh);
}
.ali-body{flex:1;min-width:0;}
.ali-cat{
  font-size:9.5px;font-weight:700;letter-spacing:1.2px;
  text-transform:uppercase;color:var(--navy);display:block;margin-bottom:4px;
}
.ali-title{
  font-family:var(--serif);font-size:16px;font-weight:600;
  color:var(--t);line-height:1.32;cursor:pointer;
  margin-bottom:5px;display:block;letter-spacing:-.15px;
}
.ali-title:hover{color:var(--navy);}
.ali-ex{
  font-size:13px;color:var(--t2);line-height:1.65;
  display:-webkit-box;-webkit-line-clamp:2;
  -webkit-box-orient:vertical;overflow:hidden;margin-bottom:5px;
}
.ali-meta{font-size:11px;color:var(--t4);}

/* ── SIDEBAR ─────────────────────────────────────────────── */
.sb-sec{margin-bottom:24px;}
.sb-title{
  font-size:10px;font-weight:700;letter-spacing:1.8px;
  text-transform:uppercase;padding-bottom:8px;
  border-bottom:2px solid var(--bd);margin-bottom:12px;color:var(--navy);
}
.tr-item{
  display:flex;gap:10px;padding:10px 0;
  border-bottom:1px solid var(--bd2);cursor:pointer;
}
.tr-item:last-child{border-bottom:none;}
.tr-num{
  font-family:var(--serif);font-size:18px;font-weight:700;
  color:var(--t3);width:22px;flex-shrink:0;line-height:1;
}
.tr-cat{font-size:9.5px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--navy);margin-bottom:2px;}
.tr-title{font-size:13px;font-weight:500;color:var(--t);line-height:1.38;}
.tr-title:hover{color:var(--navy);}
.cat-list{display:flex;flex-direction:column;gap:1px;}
.cat-btn{
  padding:7px 10px;background:none;border:none;
  text-align:left;font-size:13px;font-weight:450;color:var(--t2);
  border-radius:6px;transition:all .15s;
}
.cat-btn:hover{background:var(--bg2);color:var(--t);}
.cat-btn.on{background:var(--ac2);color:var(--navy);font-weight:600;}
.cs-item{padding:10px 0;border-bottom:1px solid var(--bd2);}
.cs-item:last-child{border-bottom:none;}
.cs-name{
  font-size:13.5px;font-weight:600;color:var(--t);
  line-height:1.3;margin:5px 0;cursor:pointer;letter-spacing:-.1px;
}
.cs-name:hover{color:var(--navy);}
.cs-meta{font-size:11px;color:var(--t4);display:flex;flex-wrap:wrap;gap:6px;}
.cs-scale{
  font-size:9px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;
  padding:2px 7px;background:var(--navy);color:#dce8f5;border-radius:4px;
}

/* ── PAGE HEADER ─────────────────────────────────────────── */
.pg-hd{
  background:var(--s2);
  border-bottom:1px solid var(--bd);
  padding:28px 0;box-shadow:var(--sh);
}
.pg-hd-in{max-width:var(--max);margin:0 auto;padding:0 20px;}
.pg-hd h1{
  font-family:var(--serif);font-size:clamp(22px,3.5vw,34px);
  font-weight:700;margin-bottom:5px;color:var(--t);letter-spacing:-.5px;
}
.pg-hd p{font-size:13.5px;color:var(--t3);font-weight:400;line-height:1.6;}

/* ── FILTER + SEARCH BAR ─────────────────────────────────── */
.fbar{background:var(--s2);border-bottom:1px solid var(--bd);overflow-x:auto;scrollbar-width:none;}
.fbar::-webkit-scrollbar{display:none;}
.fbar-in{max-width:var(--max);margin:0 auto;padding:0 20px;display:flex;}
.fbtn{
  padding:11px 14px;font-size:11px;font-weight:600;letter-spacing:.5px;
  text-transform:uppercase;color:var(--t4);border:none;background:none;
  white-space:nowrap;border-bottom:2px solid transparent;
  transition:all .15s;cursor:pointer;
}
.fbtn:hover{color:var(--t2);}
.fbtn.on{color:var(--navy);border-bottom-color:var(--navy);}
.sbar{max-width:var(--max);margin:0 auto;padding:14px 20px 0;}
.swrap{
  display:flex;align-items:center;gap:8px;
  background:var(--s2);border:1px solid var(--bd);
  border-radius:8px;padding:9px 14px;max-width:400px;
  box-shadow:var(--sh);transition:border-color .18s,box-shadow .18s;
}
.swrap:focus-within{border-color:rgba(42,79,124,.4);box-shadow:0 0 0 3px rgba(42,79,124,.08);}
.swrap input{background:none;border:none;outline:none;font-size:13.5px;color:var(--t);width:100%;font-family:inherit;}
.swrap input::placeholder{color:var(--t4);}

/* ── CONFERENCE CARD ─────────────────────────────────────── */
.cc{
  background:var(--s2);border:1px solid var(--bd);
  border-radius:12px;padding:18px 20px;margin-bottom:14px;
  box-shadow:var(--sh);transition:box-shadow .2s;
}
.cc:hover{box-shadow:var(--sh2);}
.cc-name{
  font-family:var(--serif);font-size:18px;font-weight:700;
  color:var(--t);margin-bottom:9px;line-height:1.3;letter-spacing:-.2px;
}
.cc-meta{display:flex;flex-wrap:wrap;gap:12px;font-size:12.5px;color:var(--t2);margin-bottom:9px;}
.cc-meta span{display:flex;align-items:center;gap:5px;}
.cc-tags{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:10px;}
.cc-tag{
  font-size:11px;background:var(--bg2);border:1px solid var(--bd2);
  color:var(--t3);padding:2px 9px;border-radius:20px;
}
.cc-status{font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:2px 9px;border-radius:4px;}
.cc-status.upcoming{background:rgba(52,134,58,.09);color:#34863a;}
.cc-status.ongoing{background:rgba(183,119,13,.09);color:#b7770d;}
.cc-status.past{background:var(--bg2);color:var(--t4);}
.cc-status.cancelled{background:rgba(192,57,43,.07);color:var(--red);}
.cc-scale{
  font-size:9px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;
  padding:2px 9px;background:var(--navy);color:#dce8f5;border-radius:4px;margin-left:5px;
}
.cc-web{
  display:inline-flex;align-items:center;gap:5px;font-size:12.5px;
  color:var(--navy);font-weight:600;margin-top:6px;
}
.cc-web:hover{text-decoration:underline;}

/* ── ARTICLE DETAIL ──────────────────────────────────────── */
.ad-wrap{max-width:var(--max);margin:0 auto;padding:26px 20px;display:grid;grid-template-columns:1fr 288px;gap:44px;}
@media(max-width:880px){.ad-wrap{grid-template-columns:1fr;}}
.ad-crumb{
  font-size:11.5px;color:var(--t4);margin-bottom:12px;
  display:flex;align-items:center;gap:5px;
}
.ad-crumb span{cursor:pointer;transition:color .15s;}
.ad-crumb span:hover{color:var(--navy);}
.ad-cat{
  display:inline-block;background:var(--navy);color:#dce8f5;
  font-size:9.5px;font-weight:700;letter-spacing:1.2px;
  text-transform:uppercase;padding:3px 10px;border-radius:4px;margin-bottom:11px;
}
.ad-title{
  font-family:var(--serif);font-size:clamp(22px,3vw,34px);
  font-weight:700;color:var(--t);line-height:1.22;letter-spacing:-.5px;margin-bottom:15px;
}
.ad-byline{
  display:flex;align-items:center;gap:12px;padding:11px 0;
  border-top:1px solid var(--bd2);border-bottom:1px solid var(--bd2);margin-bottom:20px;
}
.ad-avi{
  width:32px;height:32px;border-radius:50%;background:var(--navy);
  color:#dce8f5;display:flex;align-items:center;justify-content:center;
  font-size:13px;font-weight:700;flex-shrink:0;
}
.ad-auth{font-size:13.5px;font-weight:600;color:var(--t);}
.ad-role{font-size:11.5px;color:var(--t4);}
.ad-date{font-size:11.5px;color:var(--t4);margin-left:auto;text-align:right;}
.ad-img{width:100%;border-radius:10px;overflow:hidden;margin-bottom:5px;box-shadow:var(--sh);}
.ad-cap{font-size:11.5px;color:var(--t4);text-align:center;margin-bottom:20px;font-style:italic;}
.ad-body p{
  font-size:17px;line-height:1.88;color:var(--t);margin-bottom:22px;
}
.ad-body p:first-child::first-letter{
  font-size:3em;font-weight:700;float:left;line-height:.85;
  margin:3px 7px 0 0;color:var(--navy);
}
.ad-share{
  display:flex;align-items:center;gap:7px;padding:16px 0;
  border-top:1px solid var(--bd2);margin-top:18px;flex-wrap:wrap;
}
.ad-share-lbl{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:var(--t4);margin-right:3px;}
.shbtn{
  display:inline-flex;align-items:center;gap:5px;
  padding:6px 13px;border-radius:7px;font-size:12px;font-weight:600;border:none;
  cursor:pointer;transition:opacity .15s;
}
.shbtn:hover{opacity:.85;}
.sh-fb{background:#1877f2;color:#fff;}
.sh-tw{background:#000;color:#fff;}
.sh-wa{background:#25d366;color:#fff;}
.sh-cp{background:var(--bg2);color:var(--t2);border:1px solid var(--bd);}
.ad-tags{display:flex;flex-wrap:wrap;gap:5px;margin-top:14px;}
.ad-tag{
  font-size:11px;background:var(--bg2);border:1px solid var(--bd2);
  color:var(--t3);padding:3px 11px;border-radius:20px;
  cursor:pointer;transition:background .15s,color .15s,border-color .15s;
}
.ad-tag:hover{background:var(--navy);color:#dce8f5;border-color:var(--navy);}
.ad-tag.active{background:var(--navy);color:#dce8f5;border-color:var(--navy);}
/* Tag page */
.tag-hd{background:linear-gradient(135deg,var(--navy) 0%,#1a3a5f 100%);
  padding:38px 0 32px;margin-bottom:0;}
.tag-hd-in{max-width:1080px;margin:0 auto;padding:0 24px;}
.tag-hd h1{color:#fff;font-size:28px;font-weight:800;margin:0 0 6px;letter-spacing:-.3px;}
.tag-hd p{color:rgba(255,255,255,.55);font-size:15px;margin:0;}
.tag-badge{display:inline-flex;align-items:center;gap:6px;background:rgba(255,255,255,.12);
  border:1px solid rgba(255,255,255,.2);color:#fff;font-size:12px;font-weight:600;
  padding:4px 14px;border-radius:20px;margin-bottom:14px;letter-spacing:.3px;}
/* Tag cloud sidebar */
.tag-cloud{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
.tag-chip{font-size:11.5px;padding:4px 12px;border-radius:20px;
  border:1px solid var(--bd2);background:var(--bg2);color:var(--t3);
  cursor:pointer;transition:all .15s;white-space:nowrap;}
.tag-chip:hover,.tag-chip.on{background:var(--navy);color:#dce8f5;border-color:var(--navy);}
.tag-chip .tc{font-size:10px;opacity:.6;margin-left:3px;}
.rel-item{padding:11px 0;border-bottom:1px solid var(--bd2);cursor:pointer;}

.rel-item:last-child{border-bottom:none;}
.rel-cat{font-size:9.5px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--navy);margin-bottom:3px;}
.rel-title{font-size:13px;font-weight:500;color:var(--t);line-height:1.38;}
.rel-title:hover{color:var(--navy);}
.rel-date{font-size:11px;color:var(--t4);margin-top:2px;}
.more-sec{background:var(--bg2);padding:28px 0;border-top:1px solid var(--bd);}
.more-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;margin-top:14px;}
.more-card{cursor:pointer;background:var(--s2);border-radius:10px;overflow:hidden;border:1px solid var(--bd);box-shadow:var(--sh);transition:box-shadow .2s;}
.more-card:hover{box-shadow:var(--sh2);}
.more-thumb{height:128px;overflow:hidden;}
.more-card-body{padding:12px 14px;}
/* ── YOU MIGHT LIKE ── */
.yml-sec{padding:32px 0 8px;background:var(--bg);}
.yml-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;flex-wrap:wrap;gap:8px;}
.yml-title{font-size:18px;font-weight:800;color:var(--t);letter-spacing:-.3px;}
.yml-reason{font-size:11px;color:var(--t4);background:var(--bg2);border:1px solid var(--bd);
  padding:3px 10px;border-radius:20px;font-weight:500;}
.yml-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;}
@media(max-width:900px){.yml-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:540px){.yml-grid{grid-template-columns:1fr 1fr;gap:10px;}}
.yml-card{cursor:pointer;background:var(--s2);border-radius:12px;overflow:hidden;
  border:1px solid var(--bd);box-shadow:var(--sh);
  transition:transform .18s,box-shadow .18s;display:flex;flex-direction:column;}
.yml-card:hover{transform:translateY(-3px);box-shadow:var(--sh2);}
.yml-card-img{height:120px;overflow:hidden;flex-shrink:0;}
.yml-card-body{padding:11px 13px 13px;flex:1;display:flex;flex-direction:column;}
.yml-card-cat{font-size:9.5px;font-weight:700;letter-spacing:.9px;text-transform:uppercase;
  color:var(--navy);margin-bottom:5px;}
.yml-card-title{font-size:13.5px;font-weight:600;color:var(--t);line-height:1.35;
  letter-spacing:-.1px;flex:1;display:-webkit-box;-webkit-line-clamp:3;
  -webkit-box-orient:vertical;overflow:hidden;}
.yml-card-meta{font-size:11px;color:var(--t4);margin-top:7px;display:flex;gap:6px;align-items:center;}
.yml-card-score{display:none;}
.yml-match-badge{font-size:9px;font-weight:700;letter-spacing:.5px;padding:2px 6px;
  border-radius:10px;background:rgba(42,79,124,.1);color:var(--navy);white-space:nowrap;}

/* ── NEWSLETTER ──────────────────────────────────────────── */
.nl-compact{
  background:var(--ac2);border:1px solid rgba(42,79,124,.15);
  padding:18px;border-radius:10px;
}
.nl-ct{font-size:15px;font-weight:700;color:var(--navy);margin-bottom:4px;letter-spacing:-.2px;}
.nl-cs{font-size:12px;color:var(--t3);margin-bottom:12px;line-height:1.5;}
.nl-row{display:flex;gap:6px;}
.nl-inp{
  flex:1;padding:9px 12px;border:1px solid rgba(42,79,124,.2);
  border-radius:7px;font-size:13px;outline:none;min-width:0;
  background:var(--s2);font-family:inherit;color:var(--t);
  transition:border-color .18s;
}
.nl-inp:focus{border-color:var(--navy);box-shadow:0 0 0 3px rgba(42,79,124,.08);}
.nl-btn{
  background:var(--navy);color:#dce8f5;border:none;border-radius:7px;
  padding:9px 14px;font-size:12px;font-weight:600;white-space:nowrap;transition:background .15s;
}
.nl-btn:hover:not(:disabled){background:var(--navy-d);}
.nl-btn:disabled{opacity:.55;}
.nl-msg{font-size:11px;margin-top:6px;}
.nl-msg.ok{color:#34863a;}
.nl-msg.err{color:var(--red);}
.nl-ok{
  display:flex;align-items:center;gap:8px;
  background:rgba(52,134,58,.09);border:1px solid rgba(52,134,58,.22);
  color:#34863a;padding:10px 14px;border-radius:7px;font-size:13px;font-weight:600;margin-top:4px;
}

.nl-banner{
  background:var(--navy);
  border-top:none;border-bottom:none;padding:52px 20px;text-align:center;
}
.nl-inner{max-width:520px;margin:0 auto;}
.nl-icon{
  background:rgba(255,255,255,.12);width:52px;height:52px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;margin:0 auto 14px;
}
.nl-bh{
  font-family:var(--serif);font-size:clamp(17px,2.3vw,24px);
  font-weight:700;color:#fff;margin-bottom:7px;letter-spacing:-.3px;
}
.nl-bs{font-size:13.5px;color:rgba(220,232,245,.75);margin-bottom:20px;line-height:1.6;}
.nl-banner .nl-inp{
  background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.2);
  color:#fff;
}
.nl-banner .nl-inp::placeholder{color:rgba(255,255,255,.45);}
.nl-banner .nl-inp:focus{border-color:rgba(255,255,255,.5);box-shadow:none;}
.nl-banner .nl-btn{background:rgba(255,255,255,.18);color:#fff;}
.nl-banner .nl-btn:hover:not(:disabled){background:rgba(255,255,255,.26);}
.nl-banner .nl-ok{
  display:inline-flex;font-size:14px;padding:13px 20px;border-radius:8px;
  background:rgba(52,134,58,.25);border-color:rgba(52,134,58,.4);color:#a3e6a8;
}
.nl-fine{font-size:11px;color:rgba(255,255,255,.35);margin-top:12px;}

/* ── CONTACT PAGE ────────────────────────────────────────── */
.ctp{max-width:860px;margin:0 auto;padding:44px 24px;}
.ctp-hero{text-align:center;margin-bottom:48px;animation:floatUp .5s ease both;}
.ctp-hero h1{
  font-family:var(--serif);font-size:clamp(26px,4vw,38px);
  font-weight:700;color:var(--t);letter-spacing:-.5px;margin-bottom:10px;
}
.ctp-hero p{font-size:15px;color:var(--t3);max-width:500px;margin:0 auto;line-height:1.7;}
.ctp-grid{display:grid;grid-template-columns:1fr 1fr;gap:36px;margin-bottom:44px;}
@media(max-width:680px){.ctp-grid{grid-template-columns:1fr;}}
.ctp-form{
  background:var(--s2);border:1px solid var(--bd);border-radius:14px;
  padding:28px;box-shadow:var(--sh);
  animation:floatUp .5s .08s ease both;opacity:0;animation-fill-mode:forwards;
}
.ctp-form h2{font-family:var(--serif);font-size:19px;color:var(--t);margin-bottom:4px;letter-spacing:-.2px;}
.ctp-form p{font-size:12.5px;color:var(--t4);margin-bottom:20px;line-height:1.5;}
.cf-row{margin-bottom:14px;}
.cf-row label{
  display:block;font-size:10.5px;font-weight:600;letter-spacing:.5px;
  text-transform:uppercase;color:var(--t3);margin-bottom:5px;
}
.cf-inp{
  width:100%;padding:10px 13px;border:1px solid var(--bd);
  border-radius:8px;font-size:13.5px;font-family:inherit;color:var(--t);
  transition:border-color .18s,box-shadow .18s;box-sizing:border-box;
  background:var(--bg);outline:none;
}
.cf-inp:focus{border-color:rgba(42,79,124,.45);box-shadow:0 0 0 3px rgba(42,79,124,.08);background:var(--s2);}
.cf-inp.err{border-color:var(--red);}
textarea.cf-inp{resize:vertical;min-height:110px;line-height:1.6;}
.cf-honey{display:none!important;}
.cf-submit{
  width:100%;padding:11px;background:var(--navy);color:#dce8f5;border:none;
  border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;
  transition:all .18s;margin-top:6px;font-family:inherit;
}
.cf-submit:hover:not(:disabled){background:var(--navy-d);transform:translateY(-1px);box-shadow:0 4px 14px rgba(42,79,124,.25);}
.cf-submit:disabled{opacity:.55;cursor:not-allowed;transform:none;}
.cf-ok{text-align:center;padding:28px 16px;}
.cf-ok-ico{font-size:40px;margin-bottom:10px;}
.cf-ok h3{font-family:var(--serif);font-size:18px;color:var(--t);margin-bottom:6px;}
.cf-ok p{font-size:13.5px;color:var(--t3);}
.cf-err-msg{font-size:11.5px;color:var(--red);margin-top:4px;}
.ctp-info{animation:floatUp .5s .16s ease both;opacity:0;animation-fill-mode:forwards;}
.ctp-card{
  background:var(--s2);border:1px solid var(--bd);border-radius:12px;
  padding:22px;margin-bottom:14px;display:flex;gap:14px;
  align-items:flex-start;box-shadow:var(--sh);transition:box-shadow .2s;
}
.ctp-card:hover{box-shadow:var(--sh2);}
.ctp-card-ico{
  width:38px;height:38px;background:var(--ac2);
  border-radius:8px;display:flex;align-items:center;justify-content:center;
  flex-shrink:0;color:var(--navy);
}
.ctp-card h3{font-size:14px;font-weight:600;color:var(--t);margin-bottom:3px;}
.ctp-card p{font-size:12.5px;color:var(--t3);line-height:1.6;}
.ctp-card a{color:var(--navy);text-decoration:none;font-weight:600;}
.ctp-card a:hover{text-decoration:underline;}

/* ── FOOTER ──────────────────────────────────────────────── */
.ft{
  background:#1e3a5f;color:rgba(255,255,255,.5);
  padding:44px 0 20px;border-top:1px solid rgba(255,255,255,.06);
}
.ft-in{max-width:var(--max);margin:0 auto;padding:0 20px;}
.ft-grid{display:grid;grid-template-columns:1.6fr repeat(3,1fr);gap:32px;margin-bottom:36px;}
@media(max-width:680px){.ft-grid{grid-template-columns:1fr 1fr;}}
.ft-brand{
  font-family:var(--serif);font-size:19px;font-weight:700;
  color:#fff;margin-bottom:7px;letter-spacing:-.2px;
}
.ft-desc{font-size:12px;color:rgba(255,255,255,.38);line-height:1.7;}
.ft-col h4{
  font-size:9.5px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;
  color:rgba(255,255,255,.55);margin-bottom:12px;
}
.ft-col a,.ft-col span{
  display:block;font-size:12.5px;color:rgba(255,255,255,.38);
  margin-bottom:7px;transition:color .15s;cursor:pointer;
}
.ft-col a:hover,.ft-col span:hover{color:rgba(255,255,255,.75);}
.ft-col span.no-link{opacity:.25;cursor:default;}
.ft-col span.no-link:hover{color:rgba(255,255,255,.38);}
.ft-bot{
  padding-top:18px;border-top:1px solid rgba(255,255,255,.06);
  display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;
}
.ft-bot p{font-size:11px;color:rgba(255,255,255,.28);}
.dot{width:5px;height:5px;border-radius:50%;background:#22c55e;display:inline-block;animation:blink 2.5s infinite;}

/* ── MISC ────────────────────────────────────────────────── */
.skel{
  height:var(--h,16px);width:var(--w,100%);border-radius:5px;
  background:linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%);
  background-size:200% 100%;animation:shimmer 1.4s infinite;
}
.err-box{
  background:rgba(192,57,43,.07);border:1px solid rgba(192,57,43,.2);
  border-radius:8px;padding:11px 14px;
  display:flex;align-items:center;gap:9px;margin-bottom:16px;font-size:13px;color:var(--red);
}
.err-retry{
  margin-left:auto;background:none;border:1px solid rgba(192,57,43,.3);
  border-radius:5px;padding:3px 9px;font-size:12px;color:var(--red);
  cursor:pointer;display:flex;align-items:center;gap:4px;
}
.empty{text-align:center;padding:52px 20px;color:var(--t4);}
.empty h3{font-family:var(--serif);font-size:17px;color:var(--t3);margin-bottom:6px;}
`;

/* ── ARTICLE IMAGE (top-level, never re-mounts) ─────────── */
function ArticleImage({ article, height = 180, objectFit = "cover" }) {
  const realUrl = (height >= 300)
    ? (article?.image_url || article?.thumbnail_url)
    : (article?.thumbnail_url || article?.image_url);
  const W = 400, H = height, cx = W / 2, cy = H / 2;
  const s = "rgba(155,180,210,.35)", a = "rgba(155,180,210,.7)";
  const type = article?.image_style || article?.image || "atom";
  const shapes = {
    atom:   <><ellipse cx={cx} cy={cy} rx={cx*.6} ry={cy*.4} fill="none" stroke={s} strokeWidth="1.2"/><ellipse cx={cx} cy={cy} rx={cx*.6} ry={cy*.4} fill="none" stroke={s} strokeWidth="1.2" transform={`rotate(60 ${cx} ${cy})`}/><ellipse cx={cx} cy={cy} rx={cx*.6} ry={cy*.4} fill="none" stroke={s} strokeWidth="1.2" transform={`rotate(120 ${cx} ${cy})`}/><circle cx={cx} cy={cy} r="6" fill={a}/></>,
    globe:  <><circle cx={cx} cy={cy} r={Math.min(cx,cy)*.7} fill="none" stroke={s} strokeWidth="1.2"/><ellipse cx={cx} cy={cy} rx={Math.min(cx,cy)*.3} ry={Math.min(cx,cy)*.7} fill="none" stroke={s} strokeWidth="1"/><line x1={cx-Math.min(cx,cy)*.7} y1={cy} x2={cx+Math.min(cx,cy)*.7} y2={cy} stroke={s} strokeWidth="1"/></>,
    cpu:    <><rect x={cx-50} y={cy-50} width="100" height="100" rx="4" fill="none" stroke={s} strokeWidth="1.2"/><rect x={cx-30} y={cy-30} width="60" height="60" rx="2" fill="none" stroke={a} strokeWidth="1"/>{[-25,-8,8,25].map(o=><g key={o}><line x1={cx+o} y1={cy-70} x2={cx+o} y2={cy-50} stroke={s} strokeWidth="1"/><line x1={cx+o} y1={cy+50} x2={cx+o} y2={cy+70} stroke={s} strokeWidth="1"/><line x1={cx-70} y1={cy+o} x2={cx-50} y2={cy+o} stroke={s} strokeWidth="1"/><line x1={cx+50} y1={cy+o} x2={cx+70} y2={cy+o} stroke={s} strokeWidth="1"/></g>)}</>,
    shield: <><path d={`M${cx},${cy-60} L${cx+50},${cy-35} L${cx+50},${cy+10} Q${cx},${cy+65} ${cx-50},${cy+10} L${cx-50},${cy-35}Z`} fill="none" stroke={s} strokeWidth="1.2"/></>,
    chart:  <>{[0,1,2,3,4,5].map(i=>{const bh=[35,55,42,75,60,88][i];return <rect key={i} x={cx-75+i*28} y={cy+45-bh} width="20" height={bh} fill={s} rx="2"/>;})}<line x1={cx-80} y1={cy+45} x2={cx+90} y2={cy+45} stroke={s} strokeWidth="1"/></>,
  };
  if (realUrl)
    return <div style={{ width:"100%", height, overflow:"hidden", background:"#1c2c3e" }}><img src={realUrl} alt={article?.title||""} style={{ width:"100%", height:"100%", objectFit, display:"block" }} onError={e => { e.target.style.display="none"; }}/></div>;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:"block" }}>
      <defs><linearGradient id={`g${type}${H}`} x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#1c2c3e"/><stop offset="100%" stopColor="#2a4f7c"/></linearGradient></defs>
      <rect width={W} height={H} fill={`url(#g${type}${H})`}/>
      {shapes[type] || shapes.atom}
    </svg>
  );
}

/* ── NEWSLETTER BOX (top-level, no focus-loss) ───────────── */
function NewsletterBox({ compact = false }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg]     = useState("");
  const [st, setSt]       = useState(""); // "" | loading | ok | err

  const submit = async () => {
    const t = email.trim();
    if (!t || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) {
      setMsg("Please enter a valid email address."); setSt("err"); return;
    }
    setSt("loading"); setMsg("");
    try {
      const r = await fetch(`${API}/api/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: t }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409) { setMsg("This email is already subscribed."); setSt("ok"); }
      else if (r.ok || r.status === 202) {
        setMsg(d.message || "Check your inbox — a verification link has been sent! ✉️");
        setSt("ok"); setEmail("");
      } else throw new Error(d.error || "Failed to subscribe.");
    } catch (e) { setMsg(e.message); setSt("err"); }
  };

  const inputRow = (
    <>
      <div className="nl-row">
        <input id="subscribe-input" className="nl-inp" type="email" placeholder="your email address"
          value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          disabled={st === "loading"} autoComplete="email"/>
        <button className="nl-btn" onClick={submit} disabled={st === "loading"}>
          {st === "loading" ? "Registering…" : compact ? "Subscribe" : "Free Subscribe"}
        </button>
      </div>
      {msg && st !== "ok" && <p className={`nl-msg ${st}`}>{msg}</p>}
    </>
  );

  if (compact) return (
    <div className="nl-compact">
      <div className="nl-ct">Don't Miss the Latest News</div>
      <div className="nl-cs">Quantum science & tech updates straight to your inbox</div>
      {st === "ok" ? <div className="nl-ok"><Ico name="check" size={15}/>{msg}</div> : inputRow}
    </div>
  );
  return (
    <div className="nl-banner" id="newsletter">
      <div className="nl-inner">
        <div className="nl-icon"><Ico name="mail" size={24} style={{ color:"#1d4ed8" }}/></div>
        <h3 className="nl-bh">Staying Ahead of Quantum Developments</h3>
        <p className="nl-bs">A weekly summary of quantum breakthroughs, upcoming conferences, and in-depth analysis — free.</p>
        {st === "ok"
          ? <div className="nl-ok" style={{ display:"inline-flex", padding:"14px 20px", fontSize:"15px", fontWeight:600, gap:10, marginTop:0 }}><Ico name="check" size={18}/>{msg}</div>
          : inputRow}
        <p className="nl-fine">By subscribing, you agree to receive the Quantum Day newsletter. Unsubscribe anytime.</p>
      </div>
    </div>
  );
}

/* ── SEARCH BAR (top-level — THIS fixes the 1-char bug) ──── */
// When defined inside App(), React creates a new component type each render
// → React unmounts & remounts the input → cursor lost after 1 char typed
// Solution: define as a module-level function so React sees it as a stable identity
function SearchBar({ value, onChange, onClear }) {
  return (
    <div className="sbar">
      <div className="swrap">
        <Ico name="search" size={14} style={{ color:"var(--t4)", flexShrink:0 }}/>
        <input value={value} onChange={e => onChange(e.target.value)}
          placeholder="Find article, topic, author…"
          autoComplete="off" spellCheck={false}/>
        {value && <span style={{ cursor:"pointer", color:"var(--t4)", fontSize:18, lineHeight:1, flexShrink:0 }} onClick={onClear}>×</span>}
      </div>
    </div>
  );
}

const Skel = ({ h = 14, w = "100%", mb = 0 }) =>
  <div style={{ height: h, width: w, borderRadius: 5, marginBottom: mb, background: "linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }}/>;

const ErrBanner = ({ msg, onRetry }) => (
  <div className="err-box">
    <Ico name="alert" size={15} style={{ color:"#c0392b", flexShrink:0 }}/><span>{msg}</span>
    {onRetry && <button className="err-retry" onClick={onRetry}><Ico name="refresh" size={12}/>Retry</button>}
  </div>
);

/* ════════════════════════════════════════════════════════════
   MAIN APP — only routing + data state lives here
   All page components are defined at module scope below
   ════════════════════════════════════════════════════════════ */
/* ── SCROLL TO TOP BUTTON ───────────────────────────────── */
function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      title="Back to top"
      style={{
        position:"fixed", bottom:28, right:24, zIndex:9999,
        width:44, height:44, borderRadius:"50%",
        background:"var(--s2)", color:"var(--navy)",
        border:"1px solid var(--bd)",cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow:"var(--sh3)",
        transition:"opacity .25s, transform .25s",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(12px)",
      }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    </button>
  );
}


/* ── STRUCTURED DATA (SCHEMA.ORG) ───────────────────────── */
const SITE_URL = "https://quantum-day.com";

function injectSchema(id, obj) {
  // Upsert <script type="application/ld+json"> by id
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id   = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj, null, 0);
}

function removeSchema(id) {
  document.getElementById(id)?.remove();
}

function schemaOrganization(settings) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: settings.siteName || "Quantum Day",
    url: SITE_URL,
    description: settings.description || "Trusted global quantum computing news, research and events portal.",
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/logo192.png`,
      width: 192,
      height: 192,
    },
    sameAs: [
      `${SITE_URL}/rss.xml`,
    ],
  };
}

function schemaWebSite(settings) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: settings.siteName || "Quantum Day",
    url: SITE_URL,
    description: settings.description || "Trusted global quantum computing news, research and events portal.",
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/?q={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
    inLanguage: "en-US",
  };
}

function schemaWebPage(page, settings) {
  const pages = {
    news:        { name: `News | ${settings.siteName}`,        url: `${SITE_URL}/?page=news`,        desc: "Latest quantum computing news, research breakthroughs and technology updates." },
    conferences: { name: `Conferences | ${settings.siteName}`, url: `${SITE_URL}/?page=conferences`, desc: "Upcoming quantum computing conferences, workshops and academic events worldwide." },
    contact:     { name: `Contact | ${settings.siteName}`,     url: `${SITE_URL}/?page=contact`,     desc: "Contact the Quantum Day editorial team." },
  };
  const p = pages[page] || pages.news;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${p.url}#webpage`,
    name: p.name,
    url: p.url,
    description: p.desc,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-US",
  };
}

function schemaArticle(art, settings) {
  const url        = `${SITE_URL}/?article=${art.id}`;
  const headline   = (art.title || "").slice(0, 110);
  const desc       = (art.excerpt || art.content || "").replace(/\s+/g, " ").trim().slice(0, 300);
  const pubDate    = art.published_at ? new Date(art.published_at).toISOString() : new Date().toISOString();
  const modDate    = art.updated_at   ? new Date(art.updated_at).toISOString()   : pubDate;
  const authorName = art.author || settings.siteName || "Quantum Day";
  const image      = art.image_url || `${SITE_URL}/logo192.png`;

  // WordCount for timeRequired
  const wordCount  = (art.content || "").trim().split(/\s+/).filter(Boolean).length;
  const readMins   = Math.max(1, Math.ceil(wordCount / 200));

  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "@id": `${url}#article`,
    headline,
    description: desc,
    url,
    datePublished: pubDate,
    dateModified:  modDate,
    author: {
      "@type": "Person",
      name: authorName,
      jobTitle: art.author_role || "Correspondent",
      worksFor: { "@id": `${SITE_URL}/#organization` },
    },
    publisher: {
      "@type": "NewsMediaOrganization",
      "@id": `${SITE_URL}/#organization`,
      name: settings.siteName || "Quantum Day",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo192.png`,
        width: 192,
        height: 192,
      },
    },
    image: {
      "@type": "ImageObject",
      url: image,
      width: 1200,
      height: 630,
    },
    articleSection: art.category || "Technology",
    keywords: [art.category, art.tag, "quantum computing", "quantum technology"]
      .filter(Boolean).join(", "),
    wordCount,
    timeRequired: `PT${readMins}M`,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "en-US",
    copyrightYear: new Date(pubDate).getFullYear(),
    copyrightHolder: { "@id": `${SITE_URL}/#organization` },
  };
}

function schemaBreadcrumb(page, art, settings) {
  const items = [
    { "@type": "ListItem", position: 1, name: settings.siteName || "Quantum Day", item: SITE_URL },
  ];

  if (page === "news") {
    items.push({ "@type": "ListItem", position: 2, name: "News", item: `${SITE_URL}/?page=news` });
  } else if (page === "conferences") {
    items.push({ "@type": "ListItem", position: 2, name: "Conferences", item: `${SITE_URL}/?page=conferences` });
  } else if (page === "contact") {
    items.push({ "@type": "ListItem", position: 2, name: "Contact", item: `${SITE_URL}/?page=contact` });
  } else if (page === "article" && art) {
    items.push({ "@type": "ListItem", position: 2, name: "News", item: `${SITE_URL}/?page=news` });
    items.push({ "@type": "ListItem", position: 3, name: art.title, item: `${SITE_URL}/?article=${art.id}` });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}/#breadcrumb-${page}${art ? `-${art.id}` : ""}`,
    itemListElement: items,
  };
}

/* ── READ TIME CALCULATOR ───────────────────────────────── */
function calcReadTime(text="") {
  if (!text?.trim()) return "";
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const mins  = Math.ceil(words / 200);
  return mins < 1 ? "1 min" : `${mins} min`;
}

function getReadTime(article) {
  if (article?.read_time) return article.read_time;
  return calcReadTime((article?.content||"")+" "+(article?.excerpt||""));
}

/* ── "YOU MIGHT LIKE" RECOMMENDATION ENGINE ─────────────── */
function getReadHistory() {
  try {
    const stored = localStorage.getItem("qd_viewed");
    if (!stored) return new Set();
    return new Set(JSON.parse(stored));
  } catch { return new Set(); }
}

function scoreArticle(candidate, current, readHistory, avgViews) {
  let score = 0;
  const reasons = [];

  // Category match — strongest signal
  if (candidate.category && candidate.category === current.category) {
    score += 40; reasons.push("category");
  }
  // Tag match
  if (candidate.tag && current.tag && candidate.tag === current.tag) {
    score += 25; reasons.push("tag");
  }
  // Same author
  if (candidate.author && candidate.author === current.author) {
    score += 15; reasons.push("author");
  }
  // Featured boost
  if (candidate.featured) {
    score += 10; reasons.push("featured");
  }
  // Recency bonus — published within 30 days
  if (candidate.published_at) {
    const age = (Date.now() - new Date(candidate.published_at)) / 86400000;
    if (age <= 30) { score += 20; reasons.push("recent"); }
    else if (age <= 90) { score += 8; }
  }
  // Popularity boost — above average views
  if ((candidate.views || 0) > avgViews) {
    score += 10; reasons.push("popular");
  }
  // Penalise already-read articles (still show, just ranked lower)
  if (readHistory.has(String(candidate.id))) {
    score -= 30;
  }

  return { score, reasons };
}

function getReasonLabel(reasons) {
  if (reasons.includes("tag"))      return "Same topic";
  if (reasons.includes("category")) return "Same category";
  if (reasons.includes("recent"))   return "Just published";
  if (reasons.includes("featured")) return "Editor's pick";
  if (reasons.includes("popular"))  return "Trending";
  return "Recommended";
}

function getRecommendations(current, allArticles, count = 4) {
  if (!current || !allArticles.length) return [];
  const readHistory = getReadHistory();
  const candidates  = allArticles.filter(a => a.id !== current.id);
  const avgViews    = candidates.reduce((s, a) => s + (a.views || 0), 0) / (candidates.length || 1);

  const scored = candidates.map(a => ({
    ...a,
    _score:   scoreArticle(a, current, readHistory, avgViews).score,
    _reasons: scoreArticle(a, current, readHistory, avgViews).reasons,
  }));

  // Sort: score desc, then views desc as tiebreaker
  scored.sort((a, b) => b._score - a._score || (b.views || 0) - (a.views || 0));
  return scored.slice(0, count);
}

function YouMightLike({ article, articles, go }) {
  const recs = getRecommendations(article, articles, 4);
  if (!recs.length) return null;

  // Primary reason for the whole section
  const topReasons = recs[0]?._reasons || [];
  const sectionReason = topReasons.includes("tag")      ? `Based on #${article.tag}`
                      : topReasons.includes("category") ? `More in ${article.category}`
                      : topReasons.includes("recent")   ? "Fresh from our editors"
                      : "Curated for you";

  return (
    <div className="yml-sec">
      <div className="wrap">
        <div className="yml-hd">
          <span className="yml-title">✨ You Might Like</span>
          <span className="yml-reason">{sectionReason}</span>
        </div>
        <div className="yml-grid">
          {recs.map(x => (
            <div key={x.id} className="yml-card" onClick={() => go("article", x)}>
              <div className="yml-card-img">
                <ArticleImage article={x} height={120}/>
              </div>
              <div className="yml-card-body">
                <div className="yml-card-cat">{x.category}</div>
                <div className="yml-card-title">{x.title}</div>
                <div className="yml-card-meta">
                  <span>{fmt(x.published_at)}</span>
                  {x._reasons.length > 0 && (
                    <span className="yml-match-badge">
                      {getReasonLabel(x._reasons)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── VIEW COUNT DISPLAY ─────────────────────────────────── */
function fmtViews(n) {
  if (!n || n < 1) return null;
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000)    return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(n);
}

function ViewCount({ views, size = 11, showZero = false }) {
  const n = views || 0;
  const label = fmtViews(n);
  if (!label && !showZero) return null;
  const display = label || "0";
  return (
    <span className="vc" title={`${n.toLocaleString()} views`}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
      {display}
    </span>
  );
}

/* ── DARK MODE HOOK ─────────────────────────────────────── */
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const stored = localStorage.getItem('qd-dark');
      if (stored !== null) return stored === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });

  useEffect(() => {
    document.body.classList.toggle('dark', dark);
    document.documentElement.classList.toggle('dark', dark);
    try { localStorage.setItem('qd-dark', dark); } catch {}
  }, [dark]);

  // Sync with system preference change
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = e => {
      if (localStorage.getItem('qd-dark') === null) setDark(e.matches);
    };
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  return [dark, setDark];
}

/* ── READING PROGRESS BAR ───────────────────────────────── */
function ReadingProgressBar() {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el  = document.documentElement;
      const top = el.scrollTop || document.body.scrollTop;
      const h   = el.scrollHeight - el.clientHeight;
      setPct(h > 0 ? Math.min(100, (top / h) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="rpb-track">
      <div className="rpb" style={{ width: `${pct}%` }}>
        {pct > 2 && <div className="rpb-glow"/>}
      </div>
    </div>
  );
}

/* ── VIEW TRACKING (unique per visitor per 24h) ─────────── */
function shouldTrackView(articleId) {
  try {
    const key = "qd_viewed";
    const ttlKey = "qd_viewed_ts";
    const now = Date.now();
    const stored = localStorage.getItem(key);
    const ts     = parseInt(localStorage.getItem(ttlKey) || "0");

    // Reset setelah 24 jam
    let viewed = new Set();
    if (now - ts < 86400000 && stored) {
      try { viewed = new Set(JSON.parse(stored)); } catch {}
    }

    if (viewed.has(String(articleId))) return false; // already viewed

    viewed.add(String(articleId));
    localStorage.setItem(key, JSON.stringify([...viewed]));
    localStorage.setItem(ttlKey, String(now));
    return true;
  } catch {
    return true; // jika localStorage tidak tersedia, tetap track
  }
}

/* ── DARK MODE TOGGLE BUTTON ────────────────────────────── */
function DarkToggle({ dark, onToggle }) {
  return (
    <button className="dm-toggle" onClick={onToggle}
      title={dark ? "Switch to Light Mode" : "Switch to Dark Mode"}
      aria-label={dark ? "Switch to Light Mode" : "Switch to Dark Mode"}>
      {/* Sun icon — shown in dark mode */}
      <svg className="dm-icon-sun" width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1" x2="12" y2="3"/>
        <line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/>
        <line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
      {/* Moon icon — shown in light mode */}
      <svg className="dm-icon-moon" width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
    </button>
  );
}

export default function App() {
  const [dark, setDark] = useDarkMode();
  const [page, setPage]       = useState("home");
  const [art, setArt]         = useState(null);
  const [artNotFound, setArtNotFound] = useState(false);
  const [articles, setArticles] = useState([]);
  const [confs, setConfs]     = useState([]);
  const [settings, setSettings] = useState({
    siteName: "Quantum Day", tagline: "Intelligence at the Edge of Quantum",
    description: "", keywords: "", siteImages: {},
  });
  const [menus, setMenus] = useState({
    navbar: [
      { id:"h", label:"Home",        page:"home",        enabled:true },
      { id:"n", label:"News",        page:"news",        enabled:true },
      { id:"c", label:"Conferences", page:"conferences", enabled:true },
      { id:"ct", label:"Contact",    page:"contact",     enabled:true },
    ],
    footer_explore: [
      { id:"fe1", label:"Home",         page:"home",        enabled:true },
      { id:"fe2", label:"Latest News",  page:"news",        enabled:true },
      { id:"fe3", label:"Conferences",  page:"conferences", enabled:true },
    ],
    footer_topics: [
      { id:"ft1", label:"Research",   filter:"Research",   enabled:true },
      { id:"ft2", label:"Technology", filter:"Technology", enabled:true },
      { id:"ft3", label:"Security",   filter:"Security",   enabled:true },
      { id:"ft4", label:"Business",   filter:"Business",   enabled:true },
    ],
    footer_company: [
      { id:"fc1", label:"About",            url:"", page:"",        enabled:true },
      { id:"fc2", label:"Editorial Policy", url:"", page:"",        enabled:true },
      { id:"fc3", label:"Privacy Policy",   url:"", page:"",        enabled:true },
      { id:"fc4", label:"Contact Us",       url:"", page:"contact", enabled:true },
    ],
  });
  const [loadA, setLoadA]   = useState(true);
  const [loadC, setLoadC]   = useState(true);
  const [errA, setErrA]     = useState(null);
  const [errC, setErrC]     = useState(null);
  const [catF, setCatF]     = useState("All");
  const [tagF, setTagF]     = useState("");   // active tag filter
  const [q, setQ]           = useState("");
  const [srchOpen, setSrchOpen] = useState(false);
  const [mob, setMob]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [verifyToast, setVerifyToast] = useState(null);
  const [unsubModal, setUnsubModal] = useState(null);
  const srchRef = useRef(null);

  const fetchA = useCallback(async () => {
    setLoadA(true);
    try { setArticles(await db.articles()); setErrA(null); }
    catch { setErrA("Failed to load article."); }
    finally { setLoadA(false); }
  }, []);
  const fetchC = useCallback(async () => {
    setLoadC(true);
    try { setConfs(await db.conferences()); setErrC(null); }
    catch { setErrC("Failed to load conference."); }
    finally { setLoadC(false); }
  }, []);

  useEffect(() => {
    fetchA(); fetchC();
    db.settings().then(s => setSettings(s)).catch(() => {});
    db.menus().then(m => setMenus(m)).catch(() => {});
    const iv = setInterval(() => {
      fetchA(); fetchC();
      db.settings().then(s => setSettings(s)).catch(() => {});
      db.menus().then(m => setMenus(m)).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [fetchA, fetchC]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const v = p.get("verify");
    const u = p.get("unsubscribe");
    const artId = p.get("article");
    const unsubConfirm = p.get("unsubscribe_confirm");
    const unsubToken = p.get("token");

    if (unsubConfirm === "1" && unsubToken) {
      window.history.replaceState({}, "", window.location.pathname);
      setUnsubModal({ token: unsubToken });
      return;
    }
    if (!v && !u) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (v) {
      const msgs = { success:["ok","✓ Email successfully verified! Welcome to Quantum Day Newsletter."], already:["ok","Your email has been verified before."], expired:["err","Verification link has expired. Please re-register."], invalid:["err","Invalid verification link."] };
      const [st, msg] = msgs[v] || ["err","Verification failed."];
      setVerifyToast({ st, msg });
      setTimeout(() => setVerifyToast(null), 8000);
    }
    if (u) {
      const msgs = { success:["ok","✓ You have been successfully unsubscribed from Quantum Day Newsletter."], notfound:["err","Subscription not found."], invalid:["err","Invalid unsubscribe link."] };
      const [st, msg] = msgs[u] || ["err","Unsubscribe failed."];
      setVerifyToast({ st, msg });
      setTimeout(() => setVerifyToast(null), 8000);
    }
  }, []);

  useEffect(() => {
    if (!articles.length) return;
    const p = new URLSearchParams(window.location.search);
    // Deep link: ?tag=Breakthrough
    const tagParam = p.get("tag");
    if (tagParam) { setPage("news"); setTagF(tagParam); setCatF("All"); return; }
    const artId = p.get("article");
    if (!artId) return;
    const found = articles.find(a => String(a.id) === artId);
    if (!found) {
      // Article not found — show 404 state instead of blank page (prevents Soft 404)
      window.history.replaceState({}, "", window.location.pathname);
      setArtNotFound(true);
      setPage("article");
      setArt(null);
      return;
    }
    if (found) {
      setArtNotFound(false);
      window.history.replaceState({}, "", window.location.pathname);
      setPage("article");
      setArt(found);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Track view for direct URL access — unique per visitor per 24h
      if (shouldTrackView(found.id)) {
        fetch(`${API}/api/articles?id=${found.id}&action=view`, { method: "PATCH" })
          .then(r => r.ok ? r.json() : null)
          .then(d => {
            if (d?.views != null) setArticles(prev =>
              prev.map(x => x.id === found.id ? { ...x, views: d.views } : x)
            );
          })
          .catch(() => {});
      }
    }
  }, [articles]);

  useEffect(() => {
    document.title = page === "home"        ? `${settings.siteName} – ${settings.tagline}`
                   : page === "news"        ? `News | ${settings.siteName}`
                   : page === "conferences" ? `Conferences | ${settings.siteName}`
                   : art                    ? `${art.title} | ${settings.siteName}`
                   : settings.siteName;
    // ── Dynamic OG / Twitter meta tags ──────────────────────
    function setMeta(attr, key, val) {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    }
    function setLink(rel, val) {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", val);
    }

    const sName    = settings.siteName || "Quantum Day";
    const sDesc    = settings.description || "Trusted global quantum computing news, research breakthroughs, conferences and technology updates.";
    const siteOg   = settings.siteImages?.og_image || `${SITE_URL}/api/og`;

    if (page === "article" && art) {
      const artUrl = `${SITE_URL}/?article=${art.id}`;
      const ogImg  = `${SITE_URL}/api/og?id=${art.id}`;
      const desc   = (art.excerpt || sDesc).slice(0, 200);
      const ttl    = `${art.title} | ${sName}`;
      // OG
      setMeta("property", "og:type",        "article");
      setMeta("property", "og:title",       ttl);
      setMeta("property", "og:description", desc);
      setMeta("property", "og:url",         artUrl);
      setMeta("property", "og:image",       art.image_url || ogImg);
      setMeta("property", "og:image:width",  "1200");
      setMeta("property", "og:image:height", "630");
      // Twitter
      setMeta("name", "twitter:card",        "summary_large_image");
      setMeta("name", "twitter:title",       ttl);
      setMeta("name", "twitter:description", desc);
      setMeta("name", "twitter:image",       art.image_url || ogImg);
      // Article meta
      setMeta("property", "article:published_time", art.published_at || "");
      setMeta("property", "article:author",          art.author || sName);
      setMeta("property", "article:section",         art.category || "Technology");
      // Canonical
      setLink("canonical", artUrl);
      // Description
      setMeta("name", "description", desc);
    } else {
      const pageUrl  = page === "home" ? SITE_URL : `${SITE_URL}/?page=${page}`;
      const pageTitle = page === "home"        ? `${sName} – ${settings.tagline || "Intelligence at the Edge of Quantum"}`
                      : page === "news"        ? `News | ${sName}`
                      : page === "conferences" ? `Conferences | ${sName}`
                      : page === "contact"     ? `Contact | ${sName}`
                      : sName;
      const ogImg = `${SITE_URL}/api/og?title=${encodeURIComponent(pageTitle)}&category=Technology`;
      // OG
      setMeta("property", "og:type",        "website");
      setMeta("property", "og:title",       pageTitle);
      setMeta("property", "og:description", sDesc);
      setMeta("property", "og:url",         pageUrl);
      setMeta("property", "og:image",       siteOg || ogImg);
      setMeta("property", "og:image:width",  "1200");
      setMeta("property", "og:image:height", "630");
      // Twitter
      setMeta("name", "twitter:card",        "summary_large_image");
      setMeta("name", "twitter:title",       pageTitle);
      setMeta("name", "twitter:description", sDesc);
      setMeta("name", "twitter:image",       siteOg || ogImg);
      // Canonical
      setLink("canonical", pageUrl);
      // Description
      setMeta("name", "description", sDesc);
    }

    // ── Structured Data (Schema.org) ──────────────────────────
    // Always inject sitewide schemas
    injectSchema("sd-org",     schemaOrganization(settings));
    injectSchema("sd-website", schemaWebSite(settings));
    injectSchema("sd-breadcrumb", schemaBreadcrumb(page, art, settings));

    if (page === "article" && art) {
      // Article page: inject NewsArticle, remove generic WebPage
      injectSchema("sd-article", schemaArticle(art, settings));
      removeSchema("sd-webpage");
    } else {
      // Non-article pages: inject WebPage, remove article schema
      if (page !== "home") injectSchema("sd-webpage", schemaWebPage(page, settings));
      else removeSchema("sd-webpage");
      removeSchema("sd-article");
    }
  }, [page, art, settings]);

  useEffect(() => { if (srchOpen && srchRef.current) srchRef.current.focus(); }, [srchOpen]);

  const go = (p, a = null, tag = null) => {
    const valid = ["home","news","conferences","article","contact","about"];
    setPage(valid.includes(p) ? p : "home");
    setArt(a); setArtNotFound(false); setMob(false); setSrchOpen(false);
    // Tag navigation — go("news", null, "Breakthrough") filters by tag
    if (tag !== null) { setTagF(tag); setCatF("All"); }
    else if (p !== "news") { setTagF(""); }  // clear tag when leaving news
    window.scrollTo({ top:0, behavior:"smooth" });
    if (p === "article" && a?.id) {
      window.history.pushState({}, "", `?article=${a.id}`);
      // Track view — unique per visitor per 24h
      if (shouldTrackView(a.id)) {
        fetch(`${API}/api/articles?id=${a.id}&action=view`, { method: "PATCH" })
          .then(r => r.ok ? r.json() : null)
          .then(d => {
            if (d?.views != null) setArticles(prev =>
              prev.map(x => x.id === a.id ? { ...x, views: d.views } : x)
            );
          })
          .catch(() => {});
      }
    } else if (["news","conferences","contact","about"].includes(p)) {
      window.history.pushState({}, "", `?page=${p}`);
    } else {
      window.history.pushState({}, "", window.location.pathname);
    }
  };
  const goSub = () => {
    setMob(false);
    const focus = () => { document.getElementById("newsletter")?.scrollIntoView({ behavior:"smooth", block:"center" }); setTimeout(() => document.getElementById("subscribe-input")?.focus(), 350); };
    if (page !== "home") { setPage("home"); setArt(null); setTimeout(focus, 180); }
    else focus();
  };

  // Client-side safety filter — only show published articles to public
  const pubArticles  = articles.filter(a => !a.status || a.status === "published");

  const cats       = ["All", ...new Set(pubArticles.map(a => a.category).filter(Boolean))];

  // Build tag list with counts — sorted by frequency
  const tagCounts = {};
  pubArticles.forEach(a => { if (a.tag) tagCounts[a.tag] = (tagCounts[a.tag] || 0) + 1; });
  const allTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([tag, count]) => ({ tag, count }));

  const filtered   = pubArticles.filter(a => {
    const mc = catF === "All" || a.category === catF;
    const mt = !tagF || a.tag === tagF;
    const mq = !q || a.title?.toLowerCase().includes(q.toLowerCase()) || a.excerpt?.toLowerCase().includes(q.toLowerCase());
    return mc && mt && mq;
  });
  const featuredArt  = pubArticles.find(a => a.featured) || pubArticles[0];
  const trendingArts = pubArticles.slice(0, 8);

  /* — shared props bundle passed to all page components — */
  const shared = { articles:pubArticles, confs, settings, menus, loadA, loadC, errA, errC, fetchA, fetchC, catF, setCatF, tagF, setTagF, allTags, q, setQ, featuredArt, trendingArts, filtered, cats, go, goSub };

  return (
    <>
      <style>{CSS}</style>
      <ScrollToTop/>
      {verifyToast && (
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",zIndex:9999,padding:"14px 22px",borderRadius:8,fontSize:14,fontWeight:600,display:"flex",alignItems:"center",gap:10,boxShadow:"0 8px 32px rgba(0,0,0,.15)",animation:"fadeIn .3s ease",whiteSpace:"nowrap",
          background:verifyToast.st==="ok"?"rgba(52,134,58,.09)":"rgba(192,57,43,.07)",
          border:`1px solid ${verifyToast.st==="ok"?"rgba(52,134,58,.25)":"rgba(192,57,43,.22)"}`,
          color:verifyToast.st==="ok"?"#34863a":"var(--red)"}}>
          <Ico name={verifyToast.st==="ok"?"check":"alert"} size={18}/>
          {verifyToast.msg}
          <button onClick={() => setVerifyToast(null)} style={{background:"none",border:"none",cursor:"pointer",color:"inherit",marginLeft:8,fontSize:18,lineHeight:1}}>×</button>
        </div>
      )}
      {unsubModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"var(--s2)",borderRadius:14,padding:"32px 28px",maxWidth:400,width:"100%",textAlign:"center",boxShadow:"var(--sh3)",border:"1px solid var(--bd)"}}>
            <div style={{fontSize:34,marginBottom:10}}>📧</div>
            <h2 style={{fontFamily:"var(--serif)",fontSize:20,color:"var(--t)",marginBottom:8,fontWeight:700,letterSpacing:"-.2px"}}>Cancel Subscription?</h2>
            <p style={{fontSize:13.5,color:"var(--t3)",lineHeight:1.7,marginBottom:22}}>Are you sure you want to unsubscribe from <strong>Quantum Day Newsletter</strong>? You will no longer receive our weekly updates.</p>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button style={{padding:"9px 20px",borderRadius:7,border:"1px solid var(--bd)",background:"var(--bg)",color:"var(--t2)",fontSize:13.5,fontWeight:600,cursor:"pointer"}} onClick={()=>setUnsubModal(null)}>Keep Subscription</button>
              <button style={{padding:"9px 20px",borderRadius:7,border:"none",background:"var(--red)",color:"#fff",fontSize:13.5,fontWeight:600,cursor:"pointer"}} onClick={async ()=>{
                try{
                  const r = await fetch("/api/unsubscribe?json=1&token="+encodeURIComponent(unsubModal.token));
                  const d = await r.json();
                  setUnsubModal(null);
                  if (d.ok) {
                    setVerifyToast({st:"ok",msg:"✓ You have been successfully unsubscribed from Quantum Day Newsletter."});
                  } else if (d.result === "notfound") {
                    setVerifyToast({st:"err",msg:"Subscription not found. You may have already unsubscribed."});
                  } else {
                    setVerifyToast({st:"err",msg:"Unsubscribe failed. Please try again."});
                  }
                  setTimeout(()=>setVerifyToast(null),8000);
                }catch(e){
                  setUnsubModal(null);
                  setVerifyToast({st:"err",msg:"Unsubscribe failed. Please try again."});
                  setTimeout(()=>setVerifyToast(null),5000);
                }
              }}>Yes, Unsubscribe</button>
            </div>
          </div>
        </div>
      )}
      <SiteNav {...shared} srchOpen={srchOpen} setSrchOpen={setSrchOpen} mob={mob} setMob={setMob} scrolled={scrolled} page={page} srchRef={srchRef} showProgress={page==="article"&&!!art} progressBar={page==="article"&&art?<ReadingProgressBar/>:null} darkToggle={<DarkToggle dark={dark} onToggle={()=>setDark(d=>!d)}/>}/>
      {page === "home"        && <HomePage {...shared}/>}
      {page === "news"        && <NewsPage {...shared}/>}
      {page === "conferences" && <ConfsPage {...shared}/>}
      {page === "article"     && art && <ArticlePage {...shared} art={art}/>}
      {page === "article"     && !art && artNotFound && (
        <div style={{maxWidth:640,margin:"80px auto",padding:"0 24px",textAlign:"center"}}>
          <div style={{fontSize:64,marginBottom:16}}>⚛</div>
          <h1 style={{fontFamily:"var(--serif)",fontSize:28,fontWeight:700,color:"var(--t)",marginBottom:12,letterSpacing:"-.5px"}}>Article Not Found</h1>
          <p style={{fontSize:15,color:"var(--t3)",lineHeight:1.7,marginBottom:28}}>This article may have been removed or the link may be incorrect. Browse our latest quantum computing news below.</p>
          <button onClick={()=>{setArtNotFound(false);go("news");}} style={{padding:"11px 28px",background:"var(--navy)",color:"#dce8f5",border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",transition:"background .18s"}} onMouseOver={e=>e.target.style.background="var(--navy-d)"} onMouseOut={e=>e.target.style.background="var(--navy)"}>
            Browse All Articles →
          </button>
          <div style={{marginTop:16}}>
            <span onClick={()=>{setArtNotFound(false);go("home");}} style={{fontSize:13,color:"var(--t4)",cursor:"pointer",textDecoration:"underline"}}>Go to Homepage</span>
          </div>
        </div>
      )}
      {page === "contact"     && <ContactPage settings={settings} go={go}/>}
      {page === "about"       && <ContactPage settings={settings} go={go}/>}
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   MODULE-LEVEL PAGE COMPONENTS
   Defined here so React treats them as stable identities
   and never unmounts inputs due to parent re-renders
   ════════════════════════════════════════════════════════════ */

/* ── SIDEBAR ─────────────────────────────────────────────── */
function Sidebar({ articles, confs, catF, setCatF, tagF, setTagF, allTags, cats, trendingArts, go, relatedTo, showCats, showConfs }) {
  const related = relatedTo ? articles.filter(a => a.id !== relatedTo.id && a.category === relatedTo.category).slice(0, 6) : [];
  return (
    <aside>
      <div className="sb-sec"><NewsletterBox compact/></div>
      {relatedTo && related.length > 0 && (
        <div className="sb-sec">
          <div className="sb-title">Related Articles</div>
          {related.map(x => (
            <div key={x.id} className="rel-item" onClick={() => go("article", x)}>
              <div className="rel-cat">{x.category}</div>
              <div className="rel-title">{x.title}</div>
              <div className="rel-date">{fmt(x.published_at)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="sb-sec">
        <div className="sb-title">Trending Now</div>
        {trendingArts.filter(a => a.id !== relatedTo?.id).slice(0, 7).map((a, i) => (
          <div key={a.id} className="tr-item" onClick={() => go("article", a)}>
            <div className="tr-num">{i + 1}</div>
            <div><div className="tr-cat">{a.category}</div><div className="tr-title">{a.title}</div></div>
          </div>
        ))}
        {trendingArts.length === 0 && <div style={{ fontSize:13, color:"var(--t4)" }}>No articles yet.</div>}
      </div>
      {showCats && (
        <div className="sb-sec">
          <div className="sb-title">Categories</div>
          <div className="cat-list">{cats.map(c => <button key={c} className={`cat-btn${catF === c ? " on" : ""}`} onClick={() => { setCatF(c); if(setTagF) setTagF(""); }}>{c}</button>)}</div>
      {allTags?.length > 0 && (
        <div className="sb-sec">
          <div className="sb-ttl">Popular Tags</div>
          <div className="tag-cloud">
            {allTags.slice(0, 20).map(({tag, count}) => (
              <span key={tag}
                className={`tag-chip${tagF === tag ? " on" : ""}`}
                onClick={() => { if(setTagF) setTagF(tagF === tag ? "" : tag); if(setCatF) setCatF("All"); }}
                title={`${count} article${count !== 1 ? "s" : ""}`}>
                # {tag}<span className="tc">{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
        </div>
      )}
      {showConfs && confs.filter(c => c.status === "upcoming").length > 0 && (
        <div className="sb-sec">
          <div className="sb-title">Upcoming Events</div>
          {confs.filter(c => c.status === "upcoming").slice(0, 4).map(c => (
            <div key={c.id} className="cs-item" onClick={() => go("conferences")}>
              <span className="cs-scale">{c.scale}</span>
              <div className="cs-name">{c.name}</div>
              <div className="cs-meta"><span><Ico name="calendar" size={11}/>{fmtS(c.date_start)}</span><span><Ico name="pin" size={11}/>{c.location}</span></div>
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}

/* ── SITE NAV ────────────────────────────────────────────── */
function SiteNav({ menus, settings, page, go, goSub, srchOpen, setSrchOpen, mob, setMob, scrolled, q, setQ, srchRef, showProgress, progressBar, darkToggle }) {
  return (
    <>
      <div className="tb"><div className="tb-in">
        <span className="tb-date">{new Date().toLocaleDateString("en-US",{ weekday:"long", month:"long", day:"numeric", year:"numeric" })}</span>
        <div className="tb-soc">
          <a href="#" aria-label="Facebook"><Ico name="fb" size={13}/></a>
          <a href="#" aria-label="Twitter"><Ico name="tw" size={13}/></a>
        </div>
      </div></div>

      <div className="mh"><div className="mh-in">
        <div className="mh-brand" onClick={() => go("home")}>
          <div className="mh-name">{settings.siteName}</div>
          <div className="mh-tag">{settings.tagline}</div>
        </div>
        <div className="mh-right">
          <button className="mh-srch" onClick={() => setSrchOpen(true)}><Ico name="search" size={20}/></button>
          {darkToggle}
          <button className="mh-sub" onClick={goSub}>Subscribe</button>
          <button className="mh-hbtn" onClick={() => setMob(true)}><Ico name="menu" size={22}/></button>
        </div>
      </div></div>

      <div className={`nav${scrolled ? " scrolled" : ""}`}>
        <div className="nav-in">
          {(menus.navbar || []).filter(m => m.enabled).map(m => (
            <span key={m.id} className={`nav-lnk${page === m.page ? " on" : ""}`}
              onClick={() => m.page && go(m.page)}>{m.label}</span>
          ))}
        </div>
        {showProgress && progressBar}
        {!showProgress && <div style={{height:1,background:"var(--bd)"}}/>}
      </div>

      {srchOpen && (
        <div className="srch-ov" onClick={() => setSrchOpen(false)}>
          <div className="srch-box" onClick={e => e.stopPropagation()}>
            <input ref={srchRef} placeholder="Search quantum news…" value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && q.trim()) { go("news"); setSrchOpen(false); } if (e.key === "Escape") setSrchOpen(false); }}/>
            <div className="srch-hint">Press Enter to search · Esc to close</div>
          </div>
        </div>
      )}

      {mob && (
        <div className="mob-nav">
          <div className="mob-head">
            <div className="mob-logo">{settings.siteName}</div>
            <button className="mob-close" onClick={() => setMob(false)}><Ico name="x" size={22}/></button>
          </div>
          {(menus.navbar || []).filter(m => m.enabled).map(m => (
            <div key={m.id} className="mob-item" onClick={() => m.page && go(m.page)}>{m.label}</div>
          ))}
          <div className="mob-item" onClick={goSub}>Subscribe Newsletter</div>
        </div>
      )}
    </>
  );
}

/* ── SITE FOOTER ─────────────────────────────────────────── */
/* ── CONTACT PAGE ────────────────────────────────────────── */
function ContactPage({ settings, go }) {
  const [cf, setCf] = useState({ name:"", email:"", subject:"", message:"", honey:"" });
  const [errs, setErrs] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | ok | fail
  const [startTime] = useState(Date.now());

  const validate = () => {
    const e = {};
    if (!cf.name.trim())    e.name    = "Name is required";
    if (!cf.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(cf.email)) e.email = "Valid email required";
    if (!cf.subject.trim()) e.subject = "Subject is required";
    if (cf.message.trim().length < 20) e.message = "Message must be at least 20 characters";
    return e;
  };

  const submit = async () => {
    // Anti-spam: honeypot check
    if (cf.honey) return;
    // Anti-spam: time check (must take >3s to fill form — bots fill instantly)
    if (Date.now() - startTime < 3000) return;

    const e = validate();
    if (Object.keys(e).length) { setErrs(e); return; }

    setStatus("sending");
    try {
      const r = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: cf.name, email: cf.email, subject: cf.subject, message: cf.message }),
      });
      const d = await r.json();
      if (r.ok && d.ok) setStatus("ok");
      else setStatus("fail");
    } catch { setStatus("fail"); }
  };

  const inp = (k, v) => { setCf(p => ({...p, [k]:v})); setErrs(p => ({...p, [k]:""})); };

  return (
    <>
      <div className="ctp">
        <div className="ctp-hero">
          <h1>Get in Touch</h1>
          <p>Have a story tip, press release, or partnership inquiry? We'd love to hear from you.</p>
        </div>
        <div className="ctp-grid">
          <div className="ctp-form">
            {status === "ok" ? (
              <div className="cf-ok">
                <div className="cf-ok-ico">✅</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll get back to you within 1–2 business days.</p>
              </div>
            ) : (
              <>
                <h2>Send a Message</h2>
                <p>Fill in the form below and we'll respond promptly.</p>
                {/* Honeypot field — hidden from humans, bots fill it */}
                <div className="cf-honey" aria-hidden="true">
                  <input tabIndex="-1" autoComplete="off" value={cf.honey} onChange={e=>inp("honey",e.target.value)}/>
                </div>
                <div className="cf-row">
                  <label>Your Name</label>
                  <input className={`cf-inp${errs.name?" err":""}`} placeholder="John Smith" value={cf.name} onChange={e=>inp("name",e.target.value)}/>
                  {errs.name && <div className="cf-err-msg">{errs.name}</div>}
                </div>
                <div className="cf-row">
                  <label>Email Address</label>
                  <input className={`cf-inp${errs.email?" err":""}`} type="email" placeholder="john@example.com" value={cf.email} onChange={e=>inp("email",e.target.value)}/>
                  {errs.email && <div className="cf-err-msg">{errs.email}</div>}
                </div>
                <div className="cf-row">
                  <label>Subject</label>
                  <input className={`cf-inp${errs.subject?" err":""}`} placeholder="e.g. Press Release — Quantum Breakthrough" value={cf.subject} onChange={e=>inp("subject",e.target.value)}/>
                  {errs.subject && <div className="cf-err-msg">{errs.subject}</div>}
                </div>
                <div className="cf-row">
                  <label>Message</label>
                  <textarea className={`cf-inp${errs.message?" err":""}`} placeholder="Write your message here…" value={cf.message} onChange={e=>inp("message",e.target.value)}/>
                  {errs.message && <div className="cf-err-msg">{errs.message}</div>}
                </div>
                {status === "fail" && <div className="cf-err-msg" style={{marginBottom:12}}>⚠ Failed to send. Please try again or email us directly.</div>}
                <button className="cf-submit" onClick={submit} disabled={status==="sending"}>
                  {status==="sending" ? "Sending…" : "Send Message →"}
                </button>
              </>
            )}
          </div>

          <div className="ctp-info">
            <div className="ctp-card" style={{flexDirection:"column",alignItems:"flex-start",gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div className="ctp-card-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></div>
                <h3 style={{margin:0}}>Any Inquiries</h3>
              </div>
              <p style={{margin:0,color:"var(--t3)",fontSize:14,lineHeight:1.7}}>
                For editorial, press releases, advertising, partnerships, or any other matter — we're happy to hear from you.
              </p>
              <a href="mailto:contact@quantum-day.com"
                style={{display:"inline-flex",alignItems:"center",gap:8,background:"var(--navy)",color:"#dce8f5",padding:"10px 20px",borderRadius:8,fontSize:14,fontWeight:600,textDecoration:"none",marginTop:4}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                contact@quantum-day.com
              </a>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter settings={settings} menus={{}} go={go} setCatF={()=>{}}/>
    </>
  );
}


function SiteFooter({ menus, settings, go, setCatF }) {
  return (
    <footer className="ft"><div className="ft-in">
      <div className="ft-grid">
        <div>
          <div className="ft-brand">{settings.siteName}</div>
          <div className="ft-desc">{settings.description || "Trusted global quantum computing news, research and events portal."}</div>
        </div>
        <div className="ft-col">
          <h4>Explore</h4>
          {(menus.footer_explore || []).filter(m => m.enabled).map(m => (
            <span key={m.id} onClick={() => m.page && go(m.page)}>{m.label}</span>
          ))}
        </div>
        <div className="ft-col">
          <h4>Topics</h4>
          {(menus.footer_topics || []).filter(m => m.enabled).map(m => (
            <span key={m.id} onClick={() => { go("news"); setCatF(m.filter || m.label); }}>{m.label}</span>
          ))}
        </div>
        <div className="ft-col">
          <h4>Company</h4>
          {(menus.footer_company || []).filter(m => m.enabled).map(m => {
            if (m.url)  return <a  key={m.id} href={m.url} target="_blank" rel="noopener noreferrer">{m.label}</a>;
            if (m.page) return <span key={m.id} onClick={() => go(m.page)}>{m.label}</span>;
            return <span key={m.id} className="no-link">{m.label}</span>;
          })}
        </div>
      </div>
      <div className="ft-bot">
        <p>© {new Date().getFullYear()} {settings.siteName}. All rights reserved.</p>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <a href="/rss.xml" target="_blank" rel="noopener noreferrer"
            title="Subscribe to RSS Feed"
            style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,
              color:"rgba(255,255,255,.38)",textDecoration:"none",transition:"color .15s"}}
            onMouseOver={e=>e.currentTarget.style.color="rgba(255,255,255,.75)"}
            onMouseOut={e=>e.currentTarget.style.color="rgba(255,255,255,.38)"}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19.01 7.38 20 6.18 20C4.98 20 4 19.01 4 17.82a2.18 2.18 0 0 1 2.18-2.18M4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44m0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z"/>
            </svg>
            RSS Feed
          </a>
          <span style={{ fontSize:11, color:"var(--t4)", display:"flex", alignItems:"center", gap:6 }}>
            quantum-day.com <span className="dot"/>
          </span>
        </div>
      </div>
    </div></footer>
  );
}

/* ── HOME PAGE ───────────────────────────────────────────── */
function HomePage(props) {
  const { articles, settings, loadA, errA, fetchA, featuredArt, trendingArts, cats, catF, setCatF, go, goSub, menus, confs, q, setQ, filtered } = props;
  return (
    <>
      <div className="wrap"><div className="two-col">
        <main>
          {errA && <ErrBanner msg={errA} onRetry={fetchA}/>}
          {!loadA && featuredArt && (
            <div className="feat fi" style={{animationDuration:".5s"}}>
              <div className="feat-img-wrap" onClick={() => go("article", featuredArt)} style={{cursor:"pointer"}}>
                {settings.siteImages?.hero_banner
                  ? <div style={{ width:"100%", height:260, overflow:"hidden" }}><img src={settings.siteImages.hero_banner} alt="Banner" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/></div>
                  : <ArticleImage article={featuredArt} height={260}/>}
                <div style={{position:"absolute",top:14,left:14}}>
                  <span className="feat-cat">{featuredArt.category}</span>
                </div>
              </div>
              <div className="feat-body">
                <div className="feat-title" onClick={() => go("article", featuredArt)}>{featuredArt.title}</div>
                <div className="feat-ex">{featuredArt.excerpt}</div>
                <div className="feat-meta">
                  {featuredArt.author && <span>By {featuredArt.author}{featuredArt.author_role ? `, ${featuredArt.author_role}` : ""}</span>}
                  <span>{fmt(featuredArt.published_at)}</span>
                  {getReadTime(featuredArt) && <span>{getReadTime(featuredArt)} read</span>}
                  <ViewCount views={featuredArt.views}/>
                </div>
              </div>
            </div>
          )}
          <div className="sec-hd"><span className="sec-lbl">Latest News</span><div className="sec-line"/></div>
          {loadA
            ? [1,2,3,4].map(i => <div key={i} style={{ padding:"20px 0", borderBottom:"1px solid var(--bd)" }}><Skel h={11} w={80} mb={10}/><Skel h={20} mb={8}/><Skel h={13} mb={5}/><Skel h={13} w="65%"/></div>)
            : articles.filter(a => a.id !== featuredArt?.id).length === 0
              ? <div className="empty"><h3>No articles yet</h3><p>Check back later.</p></div>
              : articles.filter(a => a.id !== featuredArt?.id).slice(0, 10).map(a => (
                  <div key={a.id} className="ali fi">
                    <div className="ali-thumb"><ArticleImage article={a} height={74}/></div>
                    <div className="ali-body">
                      <span className="ali-cat">{a.category}</span>
                      <span className="ali-title" onClick={() => go("article", a)}>{a.title}</span>
                      <div className="ali-ex">{a.excerpt}</div>
                      <div className="ali-meta" style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>{fmt(a.published_at)}{a.author ? ` · By ${a.author}` : ""}{getReadTime(a) ? ` · ${getReadTime(a)} read` : ""}<ViewCount views={a.views}/></div>
                    </div>
                  </div>
                ))
          }
          {!loadA && articles.filter(a => a.id !== featuredArt?.id).length > 10 && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <button style={{padding:"9px 24px",fontSize:13,fontWeight:600,background:"var(--navy)",color:"#dce8f5",border:"none",borderRadius:8,cursor:"pointer",transition:"all .18s"}} onClick={() => go("news")} onMouseOver={e=>e.target.style.background="var(--navy-d)"} onMouseOut={e=>e.target.style.background="var(--navy)"}>
                See All News →
              </button>
            </div>
          )}
        </main>
        <Sidebar {...props} showConfs={true}/>
      </div></div>
      <NewsletterBox/>
      <SiteFooter {...props}/>
    </>
  );
}

/* ── NEWS PAGE ───────────────────────────────────────────── */
const NEWS_PER_PAGE = 10;
function NewsPage(props) {
  const { loadA, errA, fetchA, filtered, cats, catF, setCatF, tagF, setTagF, allTags, q, setQ, go } = props;
  const [newsPage, setNewsPage] = useState(1);

  // Reset to page 1 when filter or search changes
  useEffect(() => { setNewsPage(1); }, [catF, tagF, q]);

  const totalPages = Math.ceil(filtered.length / NEWS_PER_PAGE);
  const paginated  = filtered.slice((newsPage - 1) * NEWS_PER_PAGE, newsPage * NEWS_PER_PAGE);

  return (
    <>
      <div className="pg-hd"><div className="pg-hd-in">
        <h1>News &amp; Articles</h1>
        <p>Comprehensive coverage of quantum computing, cryptography, sensing, and communications.</p>
      </div></div>
      <div className="fbar"><div className="fbar-in">
        {cats.map(c => <button key={c} className={`fbtn${catF === c ? " on" : ""}`} onClick={() => { setCatF(c); setTagF(""); }}>{c}</button>)}
      </div></div>
      {/* Active tag banner */}
      {tagF && (
        <div style={{background:"var(--navy)",padding:"10px 0"}}>
          <div className="fbar-in" style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{color:"rgba(255,255,255,.6)",fontSize:12}}>Filtered by tag:</span>
            <span style={{background:"rgba(255,255,255,.15)",color:"#fff",fontSize:12,fontWeight:700,
              padding:"3px 12px",borderRadius:20,display:"flex",alignItems:"center",gap:6}}>
              # {tagF}
              <span onClick={() => setTagF("")} style={{cursor:"pointer",opacity:.7,fontSize:14,lineHeight:1}}>×</span>
            </span>
            <span style={{color:"rgba(255,255,255,.45)",fontSize:12}}>{filtered.length} article{filtered.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
      )}
      {/* SearchBar defined at module scope → React never re-mounts it → typing works */}
      <SearchBar value={q} onChange={setQ} onClear={() => setQ("")}/>
      <div className="wrap"><div className="two-col">
        <main style={{ paddingTop:18 }}>
          {errA && <ErrBanner msg={errA} onRetry={fetchA}/>}
          {loadA
            ? [1,2,3,4,5].map(i => <div key={i} style={{ padding:"20px 0", borderBottom:"1px solid var(--bd)" }}><Skel h={11} w={80} mb={10}/><Skel h={20} mb={8}/><Skel h={13} mb={5}/><Skel h={13} w="65%"/></div>)
            : filtered.length === 0
              ? <div className="empty"><h3>No articles found</h3><p>Try a different keyword or category.</p></div>
              : <>
                  {paginated.map(a => (
                    <div key={a.id} className="ali fi">
                      <div className="ali-thumb"><ArticleImage article={a} height={74}/></div>
                      <div className="ali-body">
                        <span className="ali-cat">{a.category}</span>
                        <span className="ali-title" onClick={() => go("article", a)}>{a.title}</span>
                        <div className="ali-ex">{a.excerpt}</div>
                        <div className="ali-meta" style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>{fmt(a.published_at)}{a.author ? ` · By ${a.author}` : ""}{getReadTime(a) ? ` · ${getReadTime(a)} read` : ""}<ViewCount views={a.views}/></div>
                        {a.tag && <span className={`tag-chip${tagF === a.tag ? " on" : ""}`} onClick={() => setTagF(tagF === a.tag ? "" : a.tag)}># {a.tag}</span>}
                      </div>
                    </div>
                  ))}
                  {totalPages > 1 && (
                    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"28px 0 12px",flexWrap:"wrap"}}>
                      <button onClick={() => { setNewsPage(p => Math.max(1, p-1)); window.scrollTo({top:0,behavior:"smooth"}); }}
                        disabled={newsPage === 1}
                        style={{padding:"8px 16px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--s2)",cursor:newsPage===1?"not-allowed":"pointer",fontSize:13,fontWeight:600,color:newsPage===1?"var(--t4)":"var(--navy)",opacity:newsPage===1?.5:1}}>
                        ← Prev
                      </button>
                      {Array.from({length:totalPages},(_,i)=>i+1).map(p => (
                        <button key={p} onClick={() => { setNewsPage(p); window.scrollTo({top:0,behavior:"smooth"}); }}
                          style={{width:36,height:36,borderRadius:8,border:"1px solid var(--bd)",fontSize:13,fontWeight:700,cursor:"pointer",
                            background:newsPage===p?"var(--navy)":"var(--s2)",
                            color:newsPage===p?"#dce8f5":"var(--navy)"}}>
                          {p}
                        </button>
                      ))}
                      <button onClick={() => { setNewsPage(p => Math.min(totalPages, p+1)); window.scrollTo({top:0,behavior:"smooth"}); }}
                        disabled={newsPage === totalPages}
                        style={{padding:"8px 16px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--s2)",cursor:newsPage===totalPages?"not-allowed":"pointer",fontSize:13,fontWeight:600,color:newsPage===totalPages?"var(--t4)":"var(--navy)",opacity:newsPage===totalPages?.5:1}}>
                        Next →
                      </button>
                      <span style={{fontSize:12,color:"var(--t3)",marginLeft:8}}>
                        Halaman {newsPage} dari {totalPages} · {filtered.length} artikel
                      </span>
                    </div>
                  )}
                </>
          }
        </main>
        <Sidebar {...props} showCats={true}/>
      </div></div>
      <SiteFooter {...props}/>
    </>
  );
}

/* ── CONFERENCES PAGE ────────────────────────────────────── */
function ConfsPage(props) {
  const { confs, loadC, errC, fetchC, settings, go } = props;
  const [sf, setSf] = useState("All");
  const fc = confs.filter(c => sf === "All" || c.scale === sf);
  return (
    <>
      <div className="pg-hd" style={settings.siteImages?.conferences_banner ? { backgroundImage:`url(${settings.siteImages.conferences_banner})`, backgroundSize:"cover", backgroundPosition:"center" } : {}}>
        <div className="pg-hd-in"><h1>Quantum Conferences</h1><p>International summits and regional workshops shaping the future of quantum.</p></div>
      </div>
      <div className="fbar"><div className="fbar-in">
        {["All","Global","Regional"].map(s => <button key={s} className={`fbtn${sf === s ? " on" : ""}`} onClick={() => setSf(s)}>{s}</button>)}
      </div></div>
      <div className="wrap"><div className="two-col">
        <main style={{ paddingTop:22 }}>
          {errC && <ErrBanner msg={errC} onRetry={fetchC}/>}
          {loadC
            ? [1,2,3].map(i => <div key={i} style={{ height:140, background:"var(--bg2)", borderRadius:12, marginBottom:14, backgroundImage:"linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }}/>)
            : fc.length === 0
              ? <div className="empty"><h3>No conference</h3></div>
              : fc.map(c => (
                  <div key={c.id} className="cc fi">
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:9, gap:10, flexWrap:"wrap" }}>
                      <div className="cc-name">{c.name}</div>
                      <div style={{ display:"flex", gap:7, flexShrink:0 }}>
                        <span className={`cc-status ${c.status}`}>{c.status}</span>
                        <span className="cc-scale">{c.scale}</span>
                      </div>
                    </div>
                    <div className="cc-meta">
                      <span><Ico name="calendar" size={12}/>{fmtS(c.date_start)}{c.date_end ? ` – ${fmtS(c.date_end)}` : ""}</span>
                      <span><Ico name="pin" size={12}/>{c.location}{c.venue ? `, ${c.venue}` : ""}</span>
                      {c.attendees && <span><Ico name="users" size={12}/>{c.attendees}</span>}
                    </div>
                    {c.topics?.length > 0 && <div className="cc-tags">{c.topics.map((t,i) => <span key={i} className="cc-tag">{t}</span>)}</div>}
                    {c.website && c.website !== "#" && <a className="cc-web" href={c.website} target="_blank" rel="noopener noreferrer">Official Website <Ico name="extLink" size={11}/></a>}
                  </div>
                ))
          }
        </main>
        <Sidebar {...props}/>
      </div></div>
      <SiteFooter {...props}/>
    </>
  );
}

/* ── ARTICLE DETAIL PAGE ─────────────────────────────────── */
function ArticlePage(props) {
  const { articles, art, trendingArts, go } = props;
  const a = art;
  const moreInCat = articles.filter(x => x.id !== a.id && x.category === a.category).slice(0, 3);
  const relArticles = articles.filter(x => x.id !== a.id && x.category === a.category).slice(0, 6);
  return (
    <>
      <div className="ad-wrap fi">
        <article>
          <div className="ad-crumb">
            <span onClick={() => go("home")}>Home</span><span>›</span>
            <span onClick={() => go("news")}>News</span><span>›</span>
            <span style={{ color:"var(--t4)" }}>{a.category}</span>
          </div>
          <span className="ad-cat">{a.category}</span>
          {a.tag && <span style={{ marginLeft:8, fontSize:9.5, background:"rgba(183,119,13,.09)", color:"#b7770d", fontWeight:700, padding:"3px 9px", borderRadius:4, letterSpacing:.7, textTransform:"uppercase" }}>{a.tag}</span>}
          <h1 className="ad-title">{a.title}</h1>
          <div className="ad-byline">
            <div className="ad-avi">{(a.author || "Q")[0].toUpperCase()}</div>
            <div><div className="ad-auth">By {a.author}</div>{a.author_role && <div className="ad-role">{a.author_role}</div>}</div>
            <div className="ad-date">
              {fmt(a.published_at)}
              {getReadTime(a) && <><br/><span style={{ color:"var(--t4)" }}>{getReadTime(a)} read</span></>}
              <br/><ViewCount views={a.views || 0} size={12} showZero={true}/>
            </div>
          </div>
          <div className="ad-img"><ArticleImage article={a} height={380}/></div>
          <div className="ad-cap">Quantum Day · {a.category}</div>
          <div className="ad-body">
            {(a.content || a.excerpt || "").split("\n\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          {a.tag && <div className="ad-tags">
            <span className="ad-tag" onClick={() => go("news", null, a.category)} title={`Filter by ${a.category}`}>{a.category}</span>
            <span className="ad-tag active" onClick={() => go("news", null, a.tag)} title={`See all ${a.tag} articles`}>
              # {a.tag}
            </span>
          </div>}
          <div className="ad-share">
            <span className="ad-share-lbl">Share:</span>
            <button className="shbtn sh-fb" onClick={() => { const u=`${window.location.origin}?article=${a.id}`; window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`, "_blank"); }}><Ico name="fb" size={12}/>Facebook</button>
            <button className="shbtn sh-tw" onClick={() => { const u=`${window.location.origin}?article=${a.id}`; window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(a.title)}&url=${encodeURIComponent(u)}`, "_blank"); }}><Ico name="tw" size={12}/>X</button>
            <button className="shbtn sh-wa" onClick={() => { const u=`${window.location.origin}?article=${a.id}`; window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(a.title+" "+u)}`, "_blank"); }}>WhatsApp</button>
            <button className="shbtn sh-cp" onClick={() => { const u=`${window.location.origin}?article=${a.id}`; navigator.clipboard.writeText(u).then(() => alert("Link copied!")); }}>Copy Link</button>
          </div>
        </article>

        <aside>
          <div className="sb-sec"><NewsletterBox compact/></div>
          {relArticles.length > 0 && (
            <div className="sb-sec">
              <div className="sb-title">Related Articles</div>
              {relArticles.map(x => (
                <div key={x.id} className="rel-item" onClick={() => go("article", x)}>
                  <div className="rel-cat">{x.category}</div>
                  <div className="rel-title">{x.title}</div>
                  <div className="rel-date">{fmt(x.published_at)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="sb-sec">
            <div className="sb-title">Trending Now</div>
            {trendingArts.filter(x => x.id !== a.id).slice(0, 5).map((x, i) => (
              <div key={x.id} className="tr-item" onClick={() => go("article", x)}>
                <div className="tr-num">{i + 1}</div>
                <div><div className="tr-cat">{x.category}</div><div className="tr-title">{x.title}</div></div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {moreInCat.length > 0 && (
        <div className="more-sec"><div className="wrap">
          <div className="sec-hd"><span className="sec-lbl">More in {a.category}</span><div className="sec-line"/></div>
          <div className="more-grid">
            {moreInCat.map(x => (
              <div key={x.id} className="more-card fi" onClick={() => go("article", x)}>
                <div className="more-thumb"><ArticleImage article={x} height={130}/></div>
                <div className="more-card-body"><span className="ali-cat">{x.category}</span><div style={{ fontSize:14.5, fontWeight:600, color:"var(--t)", lineHeight:1.32, marginTop:3, letterSpacing:"-.1px" }}>{x.title}</div><div style={{ fontSize:11, color:"var(--t4)", marginTop:4 }}>{fmt(x.published_at)}</div></div>
              </div>
            ))}
          </div>
        </div></div>
      )}
      <YouMightLike article={a} articles={articles} go={go}/>
      <NewsletterBox/>
      <SiteFooter {...props}/>
    </>
  );
}
